// src/pages/Operaciones.js
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { createApiClient } from '../lib/apiClient';
import { notifySuccess } from "../lib/notifications";
import { operationDeleteError } from "../lib/operationDeleteError";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserId } from "../UserIdContext";
import {
  readDepositoFromSearch,
  resolveDepositoRoute,
} from "../lib/depositoRoute";
import {
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiCalendar,
  FiRefreshCw,
  FiX,
  FiSave,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiLock,
  FiClock,
  FiArrowRightCircle,
} from "react-icons/fi";
import "./usuarios.css";
import "./TruckCreationFields.css";
import TruckLogoDropdown from "./TruckLogoDropdown";
import { getOperationDayState } from "../lib/operationDayState";
import { getOperationSiel, getOperationFacturacion as getOperacionFacturacion, withOperationControl } from "../lib/operationControls";
import OperationDapOrder from "./OperationDapOrder";
import FiscalOutChecks from "./FiscalOutChecks";
import { operationalToday, useOperationalDay } from "../lib/dayAccess";
import OperationTransferIndicator from "./OperationTransferIndicator";
import { getOperationTransfers } from "../lib/operationTransfers";
import { getTruckLogoImageFile, isTruckLogoAllowed } from "../lib/truckLogos";
import ScheduledActivities from "./ScheduledActivities";
import TodayScheduledActivities from "./TodayScheduledActivities";


const EXTRA_STYLES = `
:root{
  --bg:#07131a;
  --panel:rgba(13,25,36,.6);
  --panel-strong:rgba(13,25,36,.85);
  --panel-solid:#0d1924;
  --stroke:#113143;
  --text:#e9f2ff;
  --muted:#9bb6d9;
  --primary:#0A9C5E;
  --primary-700:#0C7A4C;
  --accent:#65f9a1;
  --neon:#42ffb3;
  --neon-soft:rgba(66,255,179,.16);
  --neon-line:#42ffb3;
  --neon-glow:rgba(66,255,179,.26);
  --board-card-border:#33cc99;
  --board-card-glow:rgba(0,255,170,.16);
  --board-card-glow-soft:rgba(0,255,170,.08);
  --radius:18px;
  --shadow:0 20px 60px rgba(0,0,0,.35);
  --t:.25s cubic-bezier(.2,.7,.2,1);
}
html, body {
  width: 100%;
  overflow-x: hidden;
}
.users-wrap{
  color:var(--text);
  padding:8px 8px 40px;
  max-width: 100vw;
  overflow-x: hidden;
}

.users-header{ display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:16px; }
.title-stack h1{ margin:0; font-size:clamp(22px,2.2vw,32px); font-weight:800; color:#fff; }
.muted, .subtitle{ margin:0; color:var(--muted); }

/* Toolbar */
.toolbar{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.calendar-wrap .users-header .btn:disabled{ opacity:.45; cursor:not-allowed; }
.input-search{
  display:flex; align-items:center; gap:8px;
  background:var(--panel-strong); border:1px solid var(--stroke);
  padding:10px 12px; border-radius:12px; min-width:260px;
}
.input-search input{ background:transparent; border:0; outline:0; color:var(--text); width:180px; cursor:pointer; }
.input-search svg{ color:var(--muted); }

.btn{
  display:inline-flex; align-items:center; gap:10px;
  border-radius:14px; padding:11px 18px; cursor:pointer;
  border:1px solid rgba(255,255,255,.08);
  background:
    radial-gradient(120% 160% at 50% 0%, rgba(255,255,255,.08), transparent 70%),
    linear-gradient(180deg, rgba(14,26,38,.95), rgba(6,11,18,.9));
  color:var(--text);
  font-weight:600;
  transition:transform .16s ease, box-shadow .16s ease, border-color .16s ease, background .16s ease;
  box-shadow:0 8px 28px rgba(0,0,0,.35);
  white-space:nowrap;
  position:relative;
  overflow:hidden;
}
.btn::after{
  content:"";
  position:absolute;
  inset:0;
  background:linear-gradient(130deg, rgba(255,255,255,.18), transparent 55%);
  opacity:0;
  transition:opacity .2s ease;
}
.btn:hover{
  transform:translateY(-1px);
  border-color:rgba(255,255,255,.25);
}
.btn:hover::after{ opacity:1; }
.btn.solid{
  background:linear-gradient(160deg, #0FB872, #0B8A56);
  border-color:rgba(255,255,255,.05);
  color:#04140e;
  box-shadow:0 12px 32px rgba(15,184,114,.35);
}
.btn.solid::after{ display:none; }
.btn.solid:hover{ transform:translateY(-2px); }
.btn.ghost{
  background:rgba(255,255,255,.04);
  border-color:rgba(255,255,255,.18);
  color:#dff7eb;
}
.btn.icon{
  width:42px; height:42px;
  display:inline-flex; justify-content:center; align-items:center;
  padding:0; border-radius:12px;
  font-size:18px;
}
.btn.icon svg{ font-size:20px; }
.btn.danger{
  background:linear-gradient(160deg, #9c0a2d, #7a0c25);
  color:#fff;
  border-color:transparent;
}
.btn.danger:hover{ box-shadow:0 10px 30px rgba(156,10,45,.35); }

/* Panel / board */
.panel{ background:var(--panel); border:1px solid var(--stroke); border-radius:var(--radius); box-shadow:var(--shadow); padding:12px; }
.empty{ text-align:center; color:var(--muted); padding:18px; }

.board-head{ display:flex; justify-content:space-between; align-items:flex-end; gap:16px; flex-wrap:wrap; margin-top:8px; margin-bottom:16px; }
.board-head-copy{ display:flex; flex-direction:column; gap:4px; }
.board-head-copy h2{ margin:0; font-size:clamp(18px,2vw,24px); color:#fff; }
.board-actions{ display:flex; gap:8px; flex-wrap:wrap; }
.board-grid-shell{
  display:flex;
  flex-direction:column;
  gap:12px;
  width:100%;
  min-width:0;
}
.board-grid-frame{
  display:grid;
  grid-template-columns:minmax(0, 1fr);
  grid-template-rows:minmax(0, 1fr);
  height:clamp(420px, calc(100vh - 250px), 880px);
  gap:12px;
  align-items:stretch;
  width:100%;
  min-width:0;
  box-sizing:border-box;
}
.board-grid-frame.has-vertical-nav{
  grid-template-columns:minmax(0, 1fr) 18px;
}
.calendar-wrap .board-grid-wrap{
  /* La barra lateral debe compartir este alto, no el alto de todo el contenido. */
  overflow:auto;
  overflow-x:auto !important;
  height:100%;
  min-height:0;
  max-height:none;
  padding:0 6px 0 0;
  overscroll-behavior:contain;
  scrollbar-width:none;
  -ms-overflow-style:none;
  min-width:0;
  max-width:100%;
  box-sizing:border-box;
}
.board-grid-wrap::-webkit-scrollbar{
  width:0;
  height:0;
}
.board-grid-scrollbar,
.board-grid-scrollbar-y{
  border:0;
  background:
    linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,.02)),
    rgba(7,18,28,.92);
  box-shadow:0 10px 24px rgba(0,0,0,.24), inset 0 0 0 1px rgba(66,255,179,.24);
}
.board-grid-scrollbar{
  height:18px;
  overflow-x:auto;
  overflow-y:hidden;
  border-radius:999px;
  scrollbar-width:thin;
  scrollbar-color:rgba(101,249,161,.55) rgba(255,255,255,.08);
  width:100%;
  min-width:0;
  max-width:100%;
  box-sizing:border-box;
}
.board-grid-scrollbar--top{ margin-bottom:2px; }
.board-grid-scrollbar--bottom{ margin-top:2px; }
.board-grid-scrollbar::-webkit-scrollbar{ height:12px; }
.board-grid-scrollbar::-webkit-scrollbar-thumb{
  background:linear-gradient(90deg, rgba(66,255,179,.42), rgba(255,255,255,.42));
  border-radius:999px;
  border:2px solid transparent;
  background-clip:padding-box;
}
.board-grid-scrollbar::-webkit-scrollbar-track{
  background:rgba(255,255,255,.08);
  border-radius:999px;
}
.board-grid-scrollbar-y{
  width:18px;
  height:100%;
  min-height:0;
  box-sizing:border-box;
  overflow-y:auto;
  overflow-x:hidden;
  border-radius:999px;
  scrollbar-width:thin;
  scrollbar-color:rgba(101,249,161,.55) rgba(255,255,255,.08);
}
.board-grid-scrollbar-y::-webkit-scrollbar{ width:12px; }
.board-grid-scrollbar-y::-webkit-scrollbar-thumb{
  background:linear-gradient(180deg, rgba(66,255,179,.42), rgba(255,255,255,.42));
  border-radius:999px;
  border:2px solid transparent;
  background-clip:padding-box;
}
.board-grid-scrollbar-y::-webkit-scrollbar-track{
  background:rgba(255,255,255,.08);
  border-radius:999px;
}

.week-tabs{ display:flex; flex-wrap:wrap; gap:10px; margin-bottom:18px; }
.week-tab{
  border:1px solid rgba(255,255,255,.12);
  border-radius:16px;
  padding:10px 14px;
  background:rgba(8,18,28,.8);
  color:var(--text);
  display:flex;
  flex-direction:column;
  align-items:flex-start;
  font-weight:600;
  cursor:pointer;
  transition:border-color .15s ease, transform .12s ease, box-shadow .12s ease;
  min-width:120px;
}
.week-tab small{ font-size:11px; opacity:.7; margin-top:4px; }
.week-tab:hover{ border-color:#2BB673; transform:translateY(-1px); }
.week-tab.active{
  border-color:#2BB673;
  box-shadow:0 10px 24px rgba(43,182,115,.25);
  background:linear-gradient(160deg, rgba(43,182,115,.15), rgba(43,182,115,.05));
}

/* Grid Lunes-SÃ¡bado */
.board-grid{
  display:grid;
  grid-template-columns:140px repeat(6, 180px);
  gap:10px;
  align-items:stretch;
  width:max-content;
  min-width:max-content;
}
@media (max-width:900px){
  .calendar-wrap .board-grid-wrap{
    padding-right:4px;
  }
  .board-grid-frame.has-vertical-nav{
    grid-template-columns:minmax(0, 1fr) 14px;
  }
  .board-grid-scrollbar-y{ width:14px; }
}
.row-label-col, .row-label, .day-head{
  position:relative;
  padding:8px 10px;
  border:2px solid var(--board-card-border);
  border-radius:18px;
  background:
    radial-gradient(110% 140% at 0% 0%, rgba(15,184,114,.16), transparent 56%),
    linear-gradient(180deg, rgba(8,24,22,.98), rgba(8,20,31,.98));
  text-align:center;
  font-weight:800;
  color:#a8c8ee;
  box-shadow:
    0 0 0 1px rgba(51,204,153,.24),
    0 10px 24px var(--board-card-glow-soft),
    0 18px 44px rgba(0,0,0,.36);
  isolation:isolate;
  overflow:visible;
}
.row-label-col::before, .row-label::before, .day-head::before{
  content:"";
  position:absolute;
  inset:-10px;
  border-radius:28px;
  background:radial-gradient(90% 120% at 50% 50%, var(--board-card-glow), transparent 72%);
  z-index:-1;
  pointer-events:none;
}
.row-label{ display:flex; flex-direction:column; gap:4px; align-items:center; justify-content:center; }
.row-label span{ font-size:14px; color:#fff; }
.row-label small{ font-size:11.5px; text-transform:uppercase; letter-spacing:.1em; color:var(--muted); }
.day-head{ text-align:center; text-transform:capitalize; }
.day-head.has-pending{
  border-color:rgba(251,146,60,.72);
  box-shadow:
    0 0 0 1px rgba(251,146,60,.2),
    0 10px 24px rgba(251,146,60,.1),
    0 18px 44px rgba(0,0,0,.36);
}
.day-week{ display:block; font-weight:900; font-size:13px; }
.day-date{ font-size:12px; opacity:.82; }
.col-resizer{
  position:absolute;
  top:0;
  right:-6px;
  width:12px;
  height:100%;
  cursor:col-resize;
  opacity:.35;
  transition:opacity .15s ease;
  user-select:none;
  z-index:3;
}
.col-resizer:hover{ opacity:1; }
.col-resizer::after{
  content:"";
  position:absolute;
  top:14px;
  bottom:14px;
  left:50%;
  width:2px;
  transform:translateX(-50%);
  border-radius:999px;
  background:rgba(155,182,217,.45);
  transition:background .15s ease;
}
.col-resizer:hover::after{ background:rgba(101,249,161,.88); }
.row-resizer{
  position:absolute;
  left:4px;
  right:4px;
  bottom:-6px;
  height:10px;
  cursor:row-resize;
  opacity:.35;
  transition:opacity .15s ease;
  z-index:3;
  user-select:none;
}
.row-resizer:hover{ opacity:1; }
.row-resizer::after{
  content:"";
  position:absolute;
  left:12px;
  right:12px;
  top:50%;
  height:2px;
  transform:translateY(-50%);
  border-radius:999px;
  background:rgba(155,182,217,.45);
  transition:background .15s ease;
}
.row-resizer:hover::after{ background:rgba(101,249,161,.88); }

.board-cell{
  position:relative;
  min-height:110px;
  padding:10px;
  border-radius:18px;
  border:2px solid var(--board-card-border);
  background:
    radial-gradient(110% 140% at 0% 0%, rgba(15,184,114,.16), transparent 56%),
    linear-gradient(180deg, rgba(8,24,22,.98), rgba(8,20,31,.98));
  box-shadow:
    0 0 0 1px rgba(51,204,153,.24),
    0 10px 24px var(--board-card-glow-soft),
    0 18px 44px rgba(0,0,0,.36);
  isolation:isolate;
  overflow:visible;
}
.board-cell::before{
  content:"";
  position:absolute;
  inset:-10px;
  border-radius:28px;
  background:radial-gradient(90% 120% at 50% 50%, var(--board-card-glow), transparent 72%);
  z-index:-1;
  pointer-events:none;
}
.board-cell:hover{
  border-color:var(--neon-line);
  box-shadow:
    0 0 0 1px rgba(66,255,179,.28),
    0 14px 30px rgba(0,255,170,.12),
    0 24px 52px rgba(0,0,0,.4);
}
.board-cell .cell-body{
  display:flex;
  flex-direction:column;
  gap:8px;
  height:100%;
  justify-content:flex-start;
  align-items:flex-start;
  position:relative;
  z-index:1;
}
.board-cell .empty{
  width:100%;
  align-self:stretch;
  text-align:center;
}
.cell-add{
  position:absolute; top:10px; right:10px;
  width:26px; height:26px; border-radius:10px; border:1px solid rgba(66,255,179,.55);
  background:rgba(10,33,27,.96); color:#edfff6;
  display:inline-flex; align-items:center; justify-content:center; cursor:pointer;
  transition:background .18s ease, border-color .18s ease;
  z-index:2;
}
.cell-add:hover{
  background:rgba(11,37,30,.98);
  border-color:rgba(66,255,179,.72);
}
.cell-add:active{ transform:none; }

.board-item{
  border:1px solid rgba(66,255,179,.34);
  border-radius:14px;
  padding:8px 10px;
  background:
    radial-gradient(120% 140% at 0% 0%, rgba(15,184,114,.1), transparent 50%),
    linear-gradient(180deg, rgba(11,28,25,.94), rgba(10,22,33,.96));
  color:#fff;
  display:flex;
  flex-direction:column;
  gap:4px;
  text-align:left;
  font-size:12px;
  transition:border-color .15s ease, background .15s ease;
  box-shadow:0 8px 18px rgba(0,0,0,.2);
  width:min(100%, 320px);
  max-width:100%;
  min-width:0;
  box-sizing:border-box;
  flex:0 0 auto;
  align-self:flex-start;
}
.board-item.comentario{
  max-width:420px;
  background:linear-gradient(145deg, rgba(148,163,184,.24), rgba(15,23,42,.96));
  border-style:dashed;
  border-color:rgba(148,163,184,.8);
}

.board-item.pending{
  border-color:#f97316;
  background:linear-gradient(145deg, rgba(248,113,113,.25), rgba(15,23,42,.9));
}
.board-item:hover{
  border-color:rgba(66,255,179,.5);
  background:
    radial-gradient(120% 140% at 0% 0%, rgba(15,184,114,.14), transparent 50%),
    linear-gradient(180deg, rgba(11,28,25,.96), rgba(10,22,33,.98));
}

.board-item-header{
  display:grid;
  grid-template-columns:minmax(0, 1fr) auto;
  align-items:flex-start;
  gap:6px;
  min-width:0;
}
.board-item-title{
  font-weight:700;
  font-size:13px;
  min-width:0;
  overflow-wrap:anywhere;
}

/* Camiones */
.truck-logos{
  display:grid;
  grid-template-columns: repeat(5, minmax(0,1fr));
  gap:3px;
  justify-items:center;
  align-items:center;
  padding-bottom:0;
}
.ops-comment-text{
  margin-top: 6px;
  font-size: 12px;
  color: var(--muted);
  font-style: italic;
}

.truck-logo{
  width:22px;
  height:22px;
  border-radius:999px;
  border:1px solid rgba(148,163,184,.6);
  background:rgba(15,23,42,.9);
  display:flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
  overflow:hidden;
  transition:border-color .15s ease,
           transform .08s ease,
           box-shadow .15s ease,
           background .15s ease;
  position:relative;
}
.truck-logo img{
  width:100%;
  height:100%;
  object-fit:cover;
}
.truck-logo span{
  font-size:11px;
}
.truck-logo:hover{
  border-color:#22c55e;
  box-shadow:0 0 0 1px rgba(34,197,94,.6);
  transform:translateY(-1px);
}
.truck-logo.pending{
  border-color:#f97316;
  box-shadow:0 0 0 1px rgba(248,113,113,.7);
}
.truck-logo.inprogress{
  border-color:#facc15;
  box-shadow:0 0 0 1px rgba(250,204,21,.7);
}
.truck-logo.finalizado{
  border-color:#22c55e;
  box-shadow:0 0 0 1px rgba(34,197,94,.7);
}
.truck-logo .mini-edit{
  position:absolute;
  top:3px;
  right:3px;
  width:16px;
  height:16px;
  border-radius:6px;
  border:1px solid rgba(255,255,255,.3);
  background:rgba(0,0,0,.4);
  color:#e9f2ff;
  display:grid;
  place-items:center;
  font-size:10px;
  opacity:0;
  transition:opacity .15s ease, transform .12s ease;
}
.truck-logo:hover .mini-edit{
  opacity:1;
  transform:translateY(-1px);
}

/* Popup Win98 */
.win98-pop{
  position:fixed;
  background:#f2f2f2;
  color:#0f172a;
  border:1px solid #6b7280;
  box-shadow:2px 2px 0 #000, 4px 4px 0 #6b7280;
  padding:10px 10px 8px;
  width:min(280px, 92vw);
  z-index:200;
  font-size:13px;
  border-radius:4px;
}
.win98-pop h4{
  margin:0 0 6px 0;
  font-size:13px;
  font-weight:800;
}
.win98-row{
  display:flex;
  align-items:center;
  gap:6px;
  margin-bottom:8px;
}
.win98-pop label{ font-weight:700; min-width:72px; }
.win98-pop select{
  flex:1;
  padding:6px 8px;
  border:1px solid #6b7280;
  border-radius:4px;
  background:#fff;
  color:#0f172a;
  font-size:12px;
  max-width:100%;
  box-sizing:border-box;
  min-width:0;
}
.win98-list{
  border:1px solid #9ca3af;
  background:#fff;
  padding:6px;
  border-radius:4px;
  max-height:160px;
  overflow:auto;
  margin-bottom:8px;
}
.win98-name{
  flex:1.2;
  font-weight:700;
  color:#0f172a;
}
.win98-subtitle{
  font-weight:700;
  margin:0 0 6px 0;
  color:#111827;
  font-size:12px;
}
.win98-empty{
  padding:6px 0 10px;
  color:#374151;
  font-size:12px;
}
.win98-actions{ display:flex; justify-content:flex-end; gap:8px; }
.win98-btn{
  padding:6px 10px;
  border:1px solid #6b7280;
  background:linear-gradient(180deg, #fff, #d1d5db);
  color:#0f172a;
  border-radius:4px;
  cursor:pointer;
  font-weight:700;
}

/* Drawer genÃ©rico */
.drawer-overlay{
  position:fixed;
  inset:0;
  background:radial-gradient(160% 120% at 50% 10%, rgba(101,249,161,.06), transparent 45%), rgba(0,0,0,.7);
  backdrop-filter:blur(4px);
  opacity:0;
  pointer-events:none;
  transition:opacity var(--t);
  z-index:60;
}
.drawer-overlay.show{ opacity:1; pointer-events:auto; }
.drawer{
  position:fixed;
  top:50%;
  left:50%;
  transform:translate(-50%, -46%) scale(.94);
  width:min(900px, 96vw);
  max-height:calc(100vh - 32px);
  background:linear-gradient(180deg, rgba(16,26,34,.96), rgba(8,14,22,.94));
  border:1px solid rgba(42,71,90,.7);
  border-radius:24px;
  box-shadow:0 36px 120px rgba(0,0,0,.62), 0 0 0 1px rgba(255,255,255,.02) inset;
  transition:transform var(--t), opacity var(--t);
  opacity:0;
  display:flex;
  flex-direction:column;
  pointer-events:none;
  overflow:hidden;
  z-index:70;
}
.drawer.ops-modal--wide{ width:min(1040px, 96vw); }
.drawer.open{
  transform:translate(-50%, -50%) scale(1);
  opacity:1;
  pointer-events:auto;
}

.drawer-header{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px;
  padding:16px 20px 14px;
  border-bottom:1px solid rgba(17,49,67,.8);
  position:sticky;
  top:0;
  background:linear-gradient(180deg, rgba(255,255,255,.02), rgba(255,255,255,0));
  z-index:2;
}
.drawer-header h3{
  margin:0;
  font-size:20px;
  letter-spacing:.2px;
}
.drawer-body{
  padding:16px 20px 14px;
  overflow:auto;
  background:radial-gradient(140% 140% at 4% 0%, rgba(101,249,161,.06), transparent 32%), var(--panel-solid);
}
.drawer-footer{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  flex-wrap:wrap;
  padding:12px 20px;
  border-top:1px solid rgba(17,49,67,.8);
  background:linear-gradient(180deg, rgba(10,16,24,.98), rgba(10,16,24,.92));
  position:sticky;
  bottom:0;
  z-index:2;
}
.drawer-footer > div{
  display:flex;
  gap:8px;
  flex-wrap:wrap;
}
.drawer .modal-panel{
  background:linear-gradient(180deg, rgba(255,255,255,.02), rgba(255,255,255,0));
  border:1px solid rgba(255,255,255,.08);
  box-shadow:0 14px 48px rgba(0,0,0,.3);
}
.drawer .modal-panel.scrollable-panel{
  max-height:320px;
  overflow:auto;
}
@media (max-width:940px){
  .form-grid.camion{ grid-template-columns:1fr 1fr; }
}
@media (max-width:680px){
  .form-grid.camion{ grid-template-columns:1fr; }
  .form-grid.camion .btn{ width:100%; justify-content:center; }
  .truck-obs-add{
    grid-template-columns:1fr;
    width:100%;
  }
  .truck-obs-add .btn{ width:100%; }
  .row-line.truck-obs-row{
    grid-template-columns:1fr;
  }
}

/* Form */
.field{ display:flex; flex-direction:column; gap:6px; margin-bottom:10px; }
.field label{ color:var(--muted); font-size:.9rem; font-weight:700; }
.field input, .field select{
  background:var(--panel-strong); color:var(--text);
  border:1px solid var(--stroke); border-radius:12px;
  padding:10px 12px; outline:none;
  transition:border-color var(--t), background var(--t), box-shadow var(--t);
}
.field input:focus, .field select:focus{ border-color:#1f6a50; background:rgba(13,25,36,.92); box-shadow:0 0 0 3px rgba(10,156,94,.18); }

.form-grid{ display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.form-grid.full{ grid-template-columns:1fr; }
.form-grid.camion{ grid-template-columns: 1.4fr 1.2fr 120px auto; }
.truck-obs-add{
  display:grid;
  grid-template-columns:minmax(280px, 520px) auto;
  gap:12px;
  align-items:center;
  width:fit-content;
  max-width:100%;
}
.truck-obs-add .btn{
  min-width:132px;
  justify-content:center;
}
.truck-obs-state{
  width:150px;
  flex:0 0 150px;
  max-width:100%;
  justify-self:start;
}

.row-line{ padding:8px 10px; border-bottom:1px solid rgba(148,163,184,.18); display:flex; align-items:center; justify-content:space-between; gap:10px; }
.row-line:last-child{ border-bottom:none; }
.row-line.truck-obs-row{
  display:grid;
  grid-template-columns:minmax(0, 1fr) auto;
  justify-content:initial;
  gap:12px;
}
.truck-obs-main{
  min-width:0;
}
.truck-obs-main input{
  width:100%;
}
.truck-obs-main b{
  display:block;
  overflow-wrap:anywhere;
}
.truck-obs-controls{
  display:flex;
  align-items:center;
  gap:8px;
  flex-wrap:wrap;
  justify-content:flex-start;
}

.logo-select{ display:flex; align-items:center; gap:8px; }
.logo-preview{
  width:40px; height:40px; border-radius:10px; border:1px solid var(--stroke);
  background:var(--panel); display:inline-flex; align-items:center; justify-content:center; overflow:hidden;
}
.logo-preview img{ width:100%; height:100%; object-fit:contain; }

/* Calendario modal */
.cal-modal-overlay{ position:fixed; inset:0; padding:16px; background:rgba(0,0,0,.65); display:flex; align-items:center; justify-content:center; overflow:hidden; z-index:80; }
.cal-modal{ width:min(920px,96vw); max-height:90vh; overflow:hidden; border-radius:22px; border:1px solid rgba(17,49,67,.9); background:linear-gradient(160deg, rgba(6,16,14,.95), rgba(7,25,20,.92)); box-shadow:0 40px 140px rgba(0,0,0,.65); display:flex; flex-direction:column; }
.cal-header{ display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid var(--stroke); gap:8px; }
.cal-body{
  flex:1 1 auto;
  min-height:0;
  position:relative;
  overflow-y:auto;
  overflow-x:hidden;
  scrollbar-gutter:stable;
  scrollbar-width:auto;
  scrollbar-color:rgba(255,255,255,.34) rgba(255,255,255,.12);
  overscroll-behavior:contain;
  touch-action:pan-y;
  -webkit-overflow-scrolling:touch;
}
.cal-scroller{
  min-height:100%;
  padding:12px 10px 12px 12px;
  display:flex;
  flex-direction:column;
  gap:14px;
}
.cal-body::-webkit-scrollbar{ width:16px; }
.cal-body::-webkit-scrollbar-thumb{
  background:rgba(255,255,255,.3);
  border-radius:999px;
  border:4px solid rgba(255,255,255,.14);
  background-clip:padding-box;
}
.cal-body::-webkit-scrollbar-thumb:hover{ background:rgba(255,255,255,.42); }
.cal-body::-webkit-scrollbar-track{
  background:rgba(255,255,255,.12);
  border-radius:999px;
  box-shadow:inset 0 0 0 1px rgba(255,255,255,.08);
}
.cal-month{
  border:1px solid rgba(17,49,67,.9);
  border-radius:18px;
  background:linear-gradient(180deg, rgba(8,18,28,.96), rgba(7,25,20,.9));
  overflow:hidden;
}
.cal-month.active{
  border-color:rgba(15,184,114,.65);
  box-shadow:0 0 0 1px rgba(15,184,114,.22), 0 20px 42px rgba(0,0,0,.26);
}
.cal-month-head{
  position:sticky;
  top:0;
  z-index:2;
  display:flex;
  align-items:center;
  justify-content:flex-start;
  gap:10px;
  padding:10px 14px;
  border-bottom:1px solid rgba(17,49,67,.9);
  border-radius:18px 18px 0 0;
  background:linear-gradient(180deg, rgba(7,25,20,.96), rgba(7,25,20,.9));
  color:#dff7eb;
  font-weight:800;
  backdrop-filter:blur(10px);
}
.cal-grid{ padding:12px; display:grid; grid-template-columns: repeat(7,1fr); gap:8px; }
.cal-weekday{ text-align:center; font-weight:800; color:var(--muted); padding:6px 0; }
.cal-cell{
  border:1px solid var(--stroke); border-radius:12px; padding:10px; min-height:88px;
  background: radial-gradient(120% 160% at 50% 0%, rgba(10,156,94,.18), rgba(14,26,38,.85));
  cursor:pointer; transition:border-color .18s ease, box-shadow .18s ease, transform .04s ease, background var(--t);
  display:flex; flex-direction:column; gap:6px; color:var(--text); text-align:left; position:relative;
}
.cal-cell:hover{ border-color:#2a3f4a; }
.cal-cell:active{ transform: translateY(1px); }
.cal-cell .daynum{ font-weight:900; font-variant-numeric: tabular-nums; font-size:20px; }
.cal-cell.muted{ opacity:.68; }
.cal-cell.today{ box-shadow: 0 0 0 1px rgba(255,255,255,.03) inset; }
.cal-cell.has-ops{
  border-color: rgba(10,156,94,.8);
  background: linear-gradient(180deg, rgba(10,156,94,.15), rgba(10,156,94,.07)), var(--panel-strong);
  box-shadow: 0 0 0 2px rgba(10,156,94,.22);
}
.cal-count{
  position:absolute;
  top:8px;
  right:8px;
  background:rgba(0,0,0,.45);
  border:1px solid rgba(255,255,255,.18);
  border-radius:10px;
  padding:3px 6px;
  font-size:11px;
  font-weight:500;
  color:#e9f2ff;
  min-width:28px;
  text-align:center;
}
.cal-cell.pending{
  border-color: #fb923c;
  background: linear-gradient(180deg, rgba(248,113,113,.25), rgba(15,23,42,.95));
  box-shadow:0 0 0 2px rgba(248,113,113,.4);
}
.cal-chip-stack{
  display:flex;
  flex-direction:column;
  align-items:flex-start;
  gap:4px;
}
.cal-chip{
  font-size:11px; align-self:flex-start; padding:4px 8px; border-radius:10px;
  background:linear-gradient(120deg, rgba(15,184,114,.22), rgba(5,22,19,.9));
  border:1px solid rgba(43,182,115,.45);
  color:#dff7eb;
  font-weight:500;
  letter-spacing:.01em;
}
.cal-footer{ display:flex; justify-content:flex-end; gap:8px; padding: 12px 16px; border-top: 1px solid var(--stroke); }

/* vista semanal compacta: nombre + logos agrupados dentro del card */
.calendar-wrap .board-item.weekly-compact{
  padding:8px;
  position:relative;
  overflow:visible;
  width:fit-content;
}
.weekly-row{
  display:flex;
  align-items:center;
  gap:6px;
  min-width:0;
  position:relative;
}
.weekly-row .board-item-title{
  display:flex;
  align-items:center;
  gap:4px;
  flex:0 1 auto;
  font-size:11px;
  white-space:normal;
  overflow-wrap:anywhere;
  line-height:1.2;
}
.weekly-logos{
  display:grid;
  grid-template-columns:repeat(var(--truck-columns, 4), minmax(0, 1fr));
  gap:4px;
  align-items:center;
  justify-content:flex-start;
  flex:0 0 auto;
  width:calc(var(--truck-columns, 4) * 20px + (var(--truck-columns, 4) - 1) * 4px);
  max-width:60%;
  min-width:0;
  overflow:visible;
}

.weekly-logo{
  width:20px; height:20px;
  border-radius:999px;
  border:1px solid rgba(148,163,184,.6);
  background:rgba(15,23,42,.9);
  display:flex;
  align-items:center;
  justify-content:center;
  cursor:default;
  overflow:hidden;
  transition:border-color .15s ease,
           transform .08s ease,
           box-shadow .15s ease,
           background .15s ease;
  position:relative;
  flex:0 0 20px;
}
.weekly-logos .weekly-logo{ width:100%; height:auto; aspect-ratio:1; min-width:0; }
.weekly-logo.editable{
  cursor:pointer;
}
.weekly-logo img{
  width:100%;
  height:100%;
  object-fit:cover;
}
  /* Botones centrales del calendario */
.toolbar-center{
  display: flex;
  justify-content: center;  /* 👈 centro real */
  align-items: center;
  gap: 12px;
  width: 100%;
}
  .cell-more{
  margin-top: 6px;
  font-size: 12px;
  color: var(--muted);
  font-weight: 800;
  opacity: .85;
}

.weekly-placeholder{ font-weight:800; font-size:12px; }
.weekly-cell-content{ display:flex; flex-direction:column; gap:10px; width:100%; min-width:0; }
.weekly-cell-records{ display:flex; flex-direction:column; gap:8px; width:100%; min-width:0; padding:2px; box-sizing:border-box; }
.weekly-observation-groups{ display:flex; flex-direction:column; gap:10px; width:100%; min-width:0; }
.weekly-observation-group{ display:flex; flex-direction:column; gap:8px; width:100%; min-width:0; }
.calendar-wrap .board-cell .cell-body{ height:auto; min-width:0; align-items:stretch; }
.calendar-wrap .board-item.comentario{ width:fit-content; font-size:11.5px; }
.calendar-wrap .board-item.comentario .board-item-title{ font-size:11px; }
.calendar-wrap .ops-comment-text{ font-size:11.5px; }
.weekly-operation-title{ color:inherit; font:inherit; font-weight:inherit; text-align:left; overflow-wrap:anywhere; }
.weekly-row .weekly-operation-title{ min-width:0; flex:0 1 auto; }
.calendar-wrap .board-grid .day-head,
.calendar-wrap .board-grid .row-label-col{ position:sticky; top:0; z-index:5; background:#0b2628; }
.calendar-wrap .board-grid .row-label{ position:sticky; left:0; z-index:4; }
.calendar-wrap .board-grid .row-label-col{ left:0; z-index:6; }
.calendar-scroll-hint{ color:#b4cadf; font-size:13px; margin:0; }

/* Small type adjustment limited to the weekly board. */
.calendar-wrap{ font-size:13px; }
.calendar-wrap .title-stack h1{ font-size:clamp(21px,2vw,27px); }
.calendar-wrap .title-stack small{ font-size:13px; }
.calendar-wrap .board-head-copy h2{ font-size:clamp(17px,1.8vw,20px); }
.calendar-wrap .btn, .calendar-wrap .week-tab{ font-size:13px; }
.calendar-wrap .week-tab small{ font-size:10.5px; }
.calendar-wrap .row-label-col{ font-size:12px; }
.calendar-wrap .row-label > span:not(.row-resizer){ font-size:12px; }
.calendar-wrap .board-cell .empty{ font-size:12px; }
.calendar-wrap .row-label small{ font-size:11px; }
.calendar-wrap .day-week{ font-size:12px; }
.calendar-wrap .day-date{ font-size:11.5px; }
.calendar-wrap .weekly-row .board-item-title,
.calendar-wrap .board-item.comentario .board-item-title{ font-size:10.5px; }
.calendar-wrap .ops-comment-text{ font-size:11px; }
.calendar-wrap .weekly-placeholder{ font-size:11.5px; }
.calendar-wrap .operation-dap-separator,
.calendar-wrap .observation-scope-separator{ font-size:9.5px; }

@media (prefers-reduced-motion: reduce){
  .btn, .board-item, .cal-cell{ transition:none !important; }
}
`;



/* Inyecta estilos una sola vez */
function useInjectStyles() {
  useEffect(() => {
    const id = "panel-operativo-extra-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = EXTRA_STYLES;
      document.head.appendChild(tag);
    }
    return () => document.getElementById(id)?.remove();
  }, []);
}

/* Helpers logos (desde /public/imagenesOperaciones) */
const LOGOS_BASE = (process.env.PUBLIC_URL || "") + "/imagenesOperaciones";
const LOGOS_PRESET = [
  "inArribado.jpg",
  "outArribado.jpg",
  "outCarga.jpg",
  "inDescarga.jpg",
  "operativaIngresoFinalizado.jpg",
  "enProcesoDeCarga.jpg",
  "enProcesoDeDescarga.jpg",
  "cargaFinalizada.jpg",
  "descargaFinalizada.jpg",
  "operativaEgresoFinalizado.jpg",
];
const BOARD_LABEL_COL_DEFAULT = 140;
const BOARD_LABEL_COL_MIN = 96;
const BOARD_DAY_COL_DEFAULT = 180;
const BOARD_DAY_COL_MIN = 150;
const BOARD_COL_MAX = 720;
const BOARD_ROW_HEIGHT_DEFAULT = 60;
const BOARD_ROW_HEIGHT_MIN = 60;

// LOGOS que ademas de marcar el camion como finalizado, deben cerrar sus contenedores
// (IMPORTANTE: cargaFinalizada/descargaFinalizada NO cierran contenedores)
const AUTO_CLOSE_LOGOS = new Set(
  ["operativaIngresoFinalizado.jpg", "operativaEgresoFinalizado.jpg"].map((f) =>
    f.toLowerCase()
  )
);

// === GRUPOS DE ESTADOS POR LOGO ===
const PENDING_LOGOS = new Set(
  ["inArribado.jpg", "outArribado.jpg", "outCarga.jpg", "inDescarga.jpg"].map((f) =>
    f.toLowerCase()
  )
);
const PROCESS_LOGOS = new Set(
  ["enProcesoDeCarga.jpg", "enProcesoDeDescarga.jpg"].map((f) => f.toLowerCase())
);
const FINAL_LOGOS = new Set(
  [
    "cargaFinalizada.jpg",
    "descargaFinalizada.jpg",
    "operativaIngresoFinalizado.jpg",
    "operativaEgresoFinalizado.jpg",
  ].map((f) => f.toLowerCase())
);
const withExt = (name) => {
  if (!name) return "";
  if (/\.(png|jpg|jpeg|gif|svg|webp)$/i.test(name)) return name;
  return name + ".jpg";
};
const logoUrl = (name, dap = false) => `${LOGOS_BASE}/${withExt(getTruckLogoImageFile(name, dap))}`;

const shouldAutoCloseLogo = (nombre) => {
  const full = withExt(nombre || "").toLowerCase();
  return AUTO_CLOSE_LOGOS.has(full);
};

// Estado de camiÃ³n en base al nombre de archivo
function estadoCamionPorLogo(nombre) {
  const full = withExt(nombre || "").toLowerCase();
  if (!full) return "pendiente";
  if (FINAL_LOGOS.has(full)) return "finalizado";
  if (PROCESS_LOGOS.has(full)) return "proceso";
  if (PENDING_LOGOS.has(full)) return "pendiente";
  return "pendiente";
}

async function loadLogoList() {
  try {
    const r = await fetch(`${LOGOS_BASE}/manifest.json`, { cache: "no-cache" });
    if (r.ok) {
      const arr = await r.json();
      if (Array.isArray(arr) && arr.length) {
        return arr.filter((f) => typeof f === "string");
      }
    }
  } catch {}
  return LOGOS_PRESET;
}

/* Filas del tablero */
const BOARD_ROWS = [
  { id: "fi-in", label: "Ingreso Impo Fiscal", kind: "ops", tipoOperacion: "FiscalImpo", tipo: "Ingreso" },
  { id: "fi-eg", label: "Egreso Impo Fiscal",  kind: "ops", tipoOperacion: "FiscalImpo", tipo: "Egreso" },

  { id: "fe-in", label: "Ingreso Expo Fiscal", kind: "ops", tipoOperacion: "FiscalExpo", tipo: "Ingreso" },
  { id: "fe-eg", label: "Egreso Expo Fiscal",  kind: "ops", tipoOperacion: "FiscalExpo", tipo: "Egreso" },
  { id: "obs-fi", label: "Otros Fiscal", kind: "obs", ambito: "fiscal" },

  { id: "na-in", label: "Ingreso Nacional", kind: "ops", tipoOperacion: "Nacional", tipo: "Ingreso" },
  { id: "na-eg", label: "Egreso Nacional",  kind: "ops", tipoOperacion: "Nacional", tipo: "Egreso" },

  { id: "obs-na", label: "Otros Nacional", kind: "obs", ambito: "nacional" },
];

/* Fechas helpers */
const fechaUTC = (fecha = new Date()) =>
  new Date(fecha).toISOString().slice(0, 10);
const HOY = operationalToday();

const ymdToDateUTC = (ymd) => new Date(ymd + "T00:00:00.000Z");
const addDays = (d, n) => {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + n);
  return x;
};
const fmt = (d) => d.toISOString().slice(0, 10);
export const getWeeklyTransferLabel = (op, fecha) => {
  return getOperationTransfers(op, fecha).summary;
};
const SIEL_LABEL = "SIEL";
const FACTURACION_LABEL = "Facturaci\u00f3n";
const CIEL_SIEL_LABEL = `Control ${SIEL_LABEL} / ${FACTURACION_LABEL}`;
const getCamionByIndex = (op, camionIndex) =>
  (Array.isArray(op?.camiones) ? op.camiones : []).find(
    (c) => Number(c?.index) === Number(camionIndex)
  ) || null;

function startOfWeekISO(ymd) {
  const d = ymdToDateUTC(ymd);
  let dow = d.getUTCDay(); // 0=dom,1=lun,...6=sab
  if (dow === 0) dow = 7;
  const start = addDays(d, -(dow - 1));
  return start;
}
function rangeWeek(ymd) {
  const s = startOfWeekISO(ymd);
  return { from: fmt(s), to: fmt(addDays(s, 7)) };
}
function rangeMonth(ymd) {
  const d = ymdToDateUTC(ymd);
  const s = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  const e = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
  return { from: fmt(s), to: fmt(e) };
}
function daysBetween(fromYmd, toYmdExcl) {
  const dates = [];
  let cur = ymdToDateUTC(fromYmd);
  const end = ymdToDateUTC(toYmdExcl);
  while (cur < end) {
    dates.push(fmt(cur));
    cur = addDays(cur, 1);
  }
  return dates;
}

/* Formateo dÃ­a (LOCAL) */
const DAY_FORMATTER = new Intl.DateTimeFormat("es-AR", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
});
const formatShort = (iso) => {
  if (!iso) return "";
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
};
const formatMonthYear = (ymd) => {
  if (!ymd) return "";
  return `${ymd.slice(5, 7)}-${ymd.slice(0, 4)}`;
};

const formatDayLabel = (ymd) => {
  try {
    return DAY_FORMATTER.format(new Date(ymd + "T00:00:00"));
  } catch {
    return ymd;
  }
};

/* Axios */
const apiOps = createApiClient({
  baseURL:
    process.env.REACT_APP_OPS_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    "https://dnbinderapi.pruebasistemasgenius.com",
  headers: { "Content-Type": "application/json" },
});

const clientesBaseURL =
  process.env.REACT_APP_CLIENTES_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_OPS_BASE_URL ||
  "https://dnbinderapi.pruebasistemasgenius.com";

const apiClientes = createApiClient({
  baseURL: clientesBaseURL,
  headers: { "Content-Type": "application/json" },
});

/* Endpoints */
const OPS = {
  LISTAR: "/api/operaciones/listar",
  LISTAR_RANGO: "/api/operaciones/listar-rango",
  STATS_RANGO: "/api/operaciones/stats-rango",
  CREAR_OP: (f) =>
    `/api/operaciones/dia/${encodeURIComponent(f)}/operaciones`,
  EDITAR_OP: (f, id) =>
    `/api/operaciones/dia/${encodeURIComponent(
      f
    )}/operaciones/${encodeURIComponent(id)}`,
  BORRAR_OP: (f, id) =>
    `/api/operaciones/dia/${encodeURIComponent(
      f
    )}/operaciones/${encodeURIComponent(id)}`,
  CREAR_OBS: (f) =>
    `/api/operaciones/dia/${encodeURIComponent(f)}/observaciones`,
  EDITAR_OBS: (f, id) =>
    `/api/operaciones/dia/${encodeURIComponent(
      f
    )}/observaciones/${encodeURIComponent(id)}`,
  BORRAR_OBS: (f, id) =>
    `/api/operaciones/dia/${encodeURIComponent(
      f
    )}/observaciones/${encodeURIComponent(id)}`,
  POSPONER_OP: (f, id) =>
    `/api/operaciones/dia/${encodeURIComponent(
      f
    )}/operaciones/${encodeURIComponent(id)}/posponer`,
};

const CLIENTES = {
  BUSCAR: (q) => `/api/clientes/buscar?q=${encodeURIComponent(q)}`,
};

/* ==== typeahead clientes ==== */
function useDebounce(value, delay = 280) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const canSearchClientQuery = (value = "") => {
  const query = String(value || "").trim();
  if (!query) return false;
  const digits = query.replace(/\D/g, "");
  return query.length >= 2 || digits.length >= 1;
};

function SelectCliente({
  value,
  onChange,
  onSelect,
  placeholder = "Buscar cliente por código, razón social o CUIT",
  disabled = false,
}) {
  const [query, setQuery] = useState(value || "");
  const [selected, setSelected] = useState(null);
  const debounced = useDebounce(query);
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (disabled) {
      setOpts([]);
      setLoading(false);
      return;
    }
    const q = (debounced || "").trim();
    if (!canSearchClientQuery(q)) {
      setOpts([]);
      setLoading(false);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await apiClientes.get(CLIENTES.BUSCAR(q));
        if (!cancel) setOpts(Array.isArray(data) ? data : []);
      } catch {
        if (!cancel) setOpts([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [debounced, disabled]);

  useEffect(() => setQuery(value || ""), [value]);

  const select = (cli) => {
    const nombre = cli?.nombreCliente || "";
    setSelected(cli || null);
    setQuery(nombre);
    onChange?.(nombre);
    onSelect?.(cli);
    setOpen(false);
  };

  const onBlur = () => {
    if (disabled) return;
    if (selected?.nombreCliente) {
      setQuery(selected.nombreCliente);
      onChange?.(selected.nombreCliente);
    }
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" && open && opts.length > 0) {
      e.preventDefault();
      select(opts[0]);
    }
  };

  return (
    <div className="field" ref={wrapRef} style={{ position: "relative" }}>
      <label>Cliente</label>
      <div
        className="input-search input-search--xs"
        onClick={() => !disabled && setOpen(true)}
        aria-disabled={disabled}
      >
        <FiSearch />
        <input
          disabled={disabled}
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            if (disabled) return;
            setQuery(e.target.value);
            onChange?.(e.target.value);
          }}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
        />
      </div>

      {selected?.nombreCliente && (
        <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>
          Cliente seleccionado:{" "}
          <b style={{ color: "var(--text)" }}>{selected.nombreCliente}</b>
        </div>
      )}

      {open && (
        <div
          className="panel"
          onMouseDown={(e) => e.preventDefault()}
          style={{
            position: "absolute",
            inset: "auto 0 0 0",
            transform: "translateY(100%)",
            zIndex: 10,
          }}
        >
          {loading && <div className="empty">Buscando¦</div>}
          {!loading && opts.length === 0 && canSearchClientQuery(query) && (
            <div className="empty">Sin resultados</div>
          )}
          {!loading &&
            opts.map((c) => (
              <button
                key={c._id || `${c.nombreCliente}-${c.CUIT || ""}`}
                className="row-line"
                onClick={() => select(c)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  borderRadius: 10,
                  border: "1px solid var(--stroke)",
                  background: "var(--panel)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <span>
                    <b>{c.nombreCliente}</b>
                    {c.codCliente ? `· ${c.codCliente}` : ""}
                  </span>
                  {c.CUIT && (
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        opacity: 0.8,
                      }}
                    >
                      {c.CUIT}
                    </span>
                  )}
                </div>
                {c.direccion && (
                  <div
                    style={{
                      color: "var(--muted)",
                      fontSize: 12,
                      marginTop: 4,
                    }}
                  >
                    {c.direccion}
                  </div>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

/* LogoSelect */
function LogoSelect({ value, onChange, logos, disabled = false, movement, dap = false, operationType }) {
  return (
    <div className="field">
      <label>Logo</label>
      <TruckLogoDropdown inPlace value={value} onChange={onChange} logos={logos}
        logoUrl={logoUrl} disabled={disabled} movement={movement} dap={dap} operationType={operationType} />
    </div>
  );
}

/* Drawer genÃ©rico */
function Drawer({ open, onClose, children, className = "" }) {
  return (
    <>
      <div
        className={`drawer-overlay ${open ? "show" : ""}`}
        onClick={onClose}
      />
      <div
        className={`drawer ${open ? "open" : ""} ${className}`.trim()}
        role="dialog" aria-hidden={!open} inert={open ? undefined : true}
      >
        {children}
      </div>
    </>
  );
}

/* ==== Calendario mensual ==== */
function buildMonthMatrix(ymdBase) {
  const d = ymdToDateUTC(ymdBase.slice(0, 7) + "-01");
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const firstDay = new Date(Date.UTC(year, month, 1));
  let dow = firstDay.getUTCDay();
  if (dow === 0) dow = 7;
  const start = addDays(firstDay, -(dow - 1));
  const weeks = [];
  let cur = start;
  for (let w = 0; w < 6; w++) {
    const row = [];
    for (let i = 0; i < 7; i++) {
      row.push(fmt(cur));
      cur = addDays(cur, 1);
    }
    weeks.push(row);
  }
  return { year, month, weeks };
}
function sameMonth(ymdA, ymdB) {
  return ymdA.slice(0, 7) === ymdB.slice(0, 7);
}
function monthKeyOf(ymd) {
  return String(ymd || "").slice(0, 7);
}
function shiftMonthYmd(ymdBase, delta) {
  const d = ymdToDateUTC(monthKeyOf(ymdBase) + "-01");
  d.setUTCMonth(d.getUTCMonth() + delta);
  return fmt(d);
}
function buildMonthWindow(centerYmd, before = 2, after = 4) {
  const months = [];
  for (let i = -before; i <= after; i++) {
    months.push(shiftMonthYmd(centerYmd, i));
  }
  return months;
}
function createEmptyMonthStats() {
  return {
    countsMap: new Map(),
    opsCountsMap: new Map(),
    obsCountsMap: new Map(),
    pendingSet: new Set(),
  };
}
function buildMonthStats(rows) {
  const stats = createEmptyMonthStats();
  rows.forEach((r) => {
    const ymd = String(r.fecha).slice(0, 10);
    const total = Number(r.total || 0);
    const ops = Number(r.ops || 0);
    const obs = Number(r.obs || 0);
    const pendientes = Number(r.pendientes || 0);

    if (total > 0) stats.countsMap.set(ymd, total);
    if (ops > 0) stats.opsCountsMap.set(ymd, ops);
    if (obs > 0) stats.obsCountsMap.set(ymd, obs);
    if (pendientes > 0) stats.pendingSet.add(ymd);
  });
  return stats;
}

function CalendarModal({
  open,
  onClose,
  valueYmd,
  onSelectYmd,
  countsMap = new Map(),
  opsCountsMap = new Map(),
  obsCountsMap = new Map(),
  pendingSet = new Set(),
  deposito,
  onGoDia, // callback para ir a operacionesDia
}) {
  const today = useOperationalDay();
  const [cursor, setCursor] = useState(valueYmd || today);
  const [monthList, setMonthList] = useState(() =>
    buildMonthWindow(valueYmd || today)
  );
  const [monthStatsCache, setMonthStatsCache] = useState(() => new Map());
  const scrollerRef = useRef(null);
  const monthRefs = useRef(new Map());
  const loadingMonthsRef = useRef(new Set());
  const extendLockRef = useRef(false);
  const scrollFrameRef = useRef(null);
  const pendingScrollKeyRef = useRef(null);
  const autoScrollRef = useRef(false);
  const pendingExtendRestoreRef = useRef(null);
  const suppressExtendRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const nextCursor = valueYmd || today;
    const nextKey = monthKeyOf(nextCursor);
    setCursor(nextCursor);
    setMonthList(buildMonthWindow(nextCursor));
    setMonthStatsCache(
      new Map([
        [
          nextKey,
          {
            countsMap: new Map(countsMap),
            opsCountsMap: new Map(opsCountsMap),
            obsCountsMap: new Map(obsCountsMap),
            pendingSet: new Set(pendingSet),
          },
        ],
      ])
    );
    loadingMonthsRef.current.clear();
    pendingScrollKeyRef.current = nextKey;
    autoScrollRef.current = true;
    pendingExtendRestoreRef.current = null;
    suppressExtendRef.current = false;
    extendLockRef.current = false;
  }, [open, valueYmd, countsMap, opsCountsMap, obsCountsMap, pendingSet, today]);

  useEffect(() => {
    if (!open) return undefined;

    const body = document.body;
    const html = document.documentElement;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;

    body.style.overflow = "hidden";
    html.style.overflow = "hidden";

    return () => {
      body.style.overflow = prevBodyOverflow;
      html.style.overflow = prevHtmlOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !monthList.length) return;

    const pendingMonths = monthList
      .map((ymd) => monthKeyOf(ymd))
      .filter(
        (key) =>
          !monthStatsCache.has(key) && !loadingMonthsRef.current.has(key)
      );

    if (!pendingMonths.length) return;

    let cancelled = false;
    const loadingSet = loadingMonthsRef.current;
    pendingMonths.forEach((key) => loadingSet.add(key));

    (async () => {
      const rows = await Promise.all(
        pendingMonths.map(async (key) => {
          try {
            const { from, to } = rangeMonth(`${key}-01`);
            const { data } = await apiOps.get(OPS.STATS_RANGO, {
              params: { desde: from, hasta: to, deposito },
            });
            return [key, buildMonthStats(Array.isArray(data) ? data : [])];
          } catch {
            return [key, createEmptyMonthStats()];
          }
        })
      );

      pendingMonths.forEach((key) => loadingSet.delete(key));
      if (cancelled) return;

      setMonthStatsCache((prev) => {
        const next = new Map(prev);
        rows.forEach(([key, stats]) => next.set(key, stats));
        return next;
      });
    })();

    return () => {
      cancelled = true;
      pendingMonths.forEach((key) => loadingSet.delete(key));
    };
  }, [deposito, monthList, monthStatsCache, open]);

  useEffect(() => {
    if (!open) return;
    const targetKey = pendingScrollKeyRef.current;
    if (!targetKey) return;

    const node = monthRefs.current.get(targetKey);
    const scroller = scrollerRef.current;
    if (!node || !scroller) return;

    scroller.scrollTo({
      top: Math.max(0, node.offsetTop - 12),
      behavior: autoScrollRef.current ? "auto" : "smooth",
    });
    pendingScrollKeyRef.current = null;
    autoScrollRef.current = false;
  }, [monthList, open]);

  useLayoutEffect(() => {
    if (!open) return;

    const snapshot = pendingExtendRestoreRef.current;
    if (!snapshot) return;

    const scroller = scrollerRef.current;
    pendingExtendRestoreRef.current = null;

    if (!scroller) {
      extendLockRef.current = false;
      suppressExtendRef.current = false;
      return;
    }

    suppressExtendRef.current = true;

    if (snapshot.direction === "up") {
      scroller.scrollTop =
        snapshot.previousTop + (scroller.scrollHeight - snapshot.previousHeight);
    } else {
      scroller.scrollTop = Math.max(
        0,
        scroller.scrollHeight -
          scroller.clientHeight -
          snapshot.previousBottomGap
      );
    }

    requestAnimationFrame(() => {
      extendLockRef.current = false;
      requestAnimationFrame(() => {
        suppressExtendRef.current = false;
      });
    });
  }, [monthList, open]);

  useEffect(
    () => () => {
      if (scrollFrameRef.current) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    []
  );

  const syncCursorToScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const marker = scroller.getBoundingClientRect().top + scroller.clientHeight * 0.28;
    let nextKey = monthKeyOf(cursor);
    let bestDistance = Number.POSITIVE_INFINITY;

    monthRefs.current.forEach((node, key) => {
      if (!node) return;
      const distance = Math.abs(node.getBoundingClientRect().top - marker);
      if (distance < bestDistance) {
        bestDistance = distance;
        nextKey = key;
      }
    });

    setCursor((prev) =>
      monthKeyOf(prev) === nextKey ? prev : `${nextKey}-01`
    );
  };

  const extendMonths = (direction) => {
    const scroller = scrollerRef.current;
    if (!scroller || extendLockRef.current) return;

    const snapshot = {
      direction,
      previousHeight: scroller.scrollHeight,
      previousTop: scroller.scrollTop,
      previousBottomGap:
        scroller.scrollHeight - (scroller.scrollTop + scroller.clientHeight),
    };

    extendLockRef.current = true;
    pendingExtendRestoreRef.current = snapshot;
    if (direction === "up") {
      setMonthList((prev) => {
        const first = prev[0] || cursor;
        const extra = [];
        for (let i = 3; i >= 1; i--) extra.push(shiftMonthYmd(first, -i));
        return [...extra, ...prev];
      });
      return;
    }

    setMonthList((prev) => {
      const last = prev[prev.length - 1] || cursor;
      const extra = [];
      for (let i = 1; i <= 3; i++) extra.push(shiftMonthYmd(last, i));
      return [...prev, ...extra];
    });
  };

  const ensureMonthInView = (targetYmd) => {
    const targetKey = monthKeyOf(targetYmd);
    setMonthList((prev) => {
      if (!prev.length) return buildMonthWindow(targetYmd);
      if (prev.some((ymd) => monthKeyOf(ymd) === targetKey)) return prev;

      let next = [...prev];
      while (targetKey < monthKeyOf(next[0])) {
        const first = next[0];
        const extra = [];
        for (let i = 3; i >= 1; i--) extra.push(shiftMonthYmd(first, -i));
        next = [...extra, ...next];
      }
      while (targetKey > monthKeyOf(next[next.length - 1])) {
        const last = next[next.length - 1];
        const extra = [];
        for (let i = 1; i <= 3; i++) extra.push(shiftMonthYmd(last, i));
        next = [...next, ...extra];
      }
      return next;
    });
  };

  const focusMonth = (delta) => {
    const target = shiftMonthYmd(cursor, delta);
    setCursor(target);
    ensureMonthInView(target);
    pendingScrollKeyRef.current = monthKeyOf(target);
    autoScrollRef.current = false;
    requestAnimationFrame(() => {
      const node = monthRefs.current.get(monthKeyOf(target));
      const scroller = scrollerRef.current;
      if (!node || !scroller) return;
      scroller.scrollTo({
        top: Math.max(0, node.offsetTop - 12),
        behavior: "smooth",
      });
      pendingScrollKeyRef.current = null;
    });
  };

  const handleMonthScroll = () => {
    if (scrollFrameRef.current) cancelAnimationFrame(scrollFrameRef.current);
    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      const scroller = scrollerRef.current;
      if (!scroller) return;

      if (suppressExtendRef.current) {
        syncCursorToScroll();
        return;
      }

      const topGap = scroller.scrollTop;
      const bottomGap =
        scroller.scrollHeight - (scroller.scrollTop + scroller.clientHeight);

      if (topGap < 180) extendMonths("up");
      if (bottomGap < 180) extendMonths("down");
      syncCursorToScroll();
    });
  };

  const prevMonth = () => focusMonth(-1);
  const nextMonth = () => focusMonth(1);
  const goToday = () => {
    setCursor(today);
    ensureMonthInView(today);
    pendingScrollKeyRef.current = monthKeyOf(today);
    autoScrollRef.current = false;
    onSelectYmd?.(today);
    onGoDia?.(today);
    onClose?.();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="cal-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cal-header">
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className="btn icon"
              onClick={prevMonth}
              aria-label="Mes anterior"
            >
              <FiChevronLeft />
            </button>
            <div className="input-search" style={{ cursor: "default" }}>
              <FiCalendar />
              <input value={formatMonthYear(cursor)} readOnly />
            </div>
            <button
              className="btn icon"
              onClick={nextMonth}
              aria-label="Mes siguiente"
            >
              <FiChevronRight />
            </button>
            <button className="btn" onClick={goToday}>
              Hoy
            </button>
            <button className="btn" onClick={onClose}>
              Semanal
            </button>
          </div>
          <button className="btn icon" onClick={onClose} aria-label="Cerrar">
            <FiX />
          </button>
        </div>

        <div className="cal-body" ref={scrollerRef} onScroll={handleMonthScroll}>
          <div className="cal-scroller">
            {monthList.map((monthYmd) => {
              const monthKey = monthKeyOf(monthYmd);
              const { weeks: monthWeeks } = buildMonthMatrix(monthYmd);
              const monthStats =
                monthStatsCache.get(monthKey) || createEmptyMonthStats();

              return (
                <section
                  key={monthKey}
                  className={
                    "cal-month" +
                    (monthKey === monthKeyOf(cursor) ? " active" : "")
                  }
                  ref={(node) => {
                    if (node) monthRefs.current.set(monthKey, node);
                    else monthRefs.current.delete(monthKey);
                  }}
                >
                  <div className="cal-month-head">
                    <span>{formatMonthYear(monthYmd)}</span>
                  </div>

                  <div className="cal-grid">
                    {["Lun", "Mar", "Mier", "Jue", "Vie", "Sab", "Dom"].map(
                      (d) => (
                        <div key={`${monthKey}-${d}`} className="cal-weekday">
                          {d}
                        </div>
                      )
                    )}
                    {monthWeeks.flat().map((ymd) => {
                      const isMuted = !sameMonth(ymd, monthYmd);
                      const isToday = ymd === today;
                      const total = monthStats.countsMap.get(ymd) || 0;
                      const ops = monthStats.opsCountsMap.get(ymd) || 0;
                      const obs =
                        monthStats.obsCountsMap.get(ymd) ??
                        Math.max(0, total - ops);
                      const hasOps = ops > 0;
                      const hasPending = ymd >= today && monthStats.pendingSet.has(ymd);

                      const cls = [
                        "cal-cell",
                        isMuted ? "muted" : "",
                        isToday ? "today" : "",
                        hasOps ? "has-ops" : "",
                        hasPending ? "pending" : "",
                      ]
                        .join(" ")
                        .trim();

                      return (
                        <button
                          key={ymd}
                          className={cls}
                          data-date={ymd}
                          onClick={() => {
                            onSelectYmd?.(ymd);
                            onGoDia?.(ymd);
                            onClose?.();
                          }}
                          title={`${ymd} · ${ops} Ops. · ${obs} Obs.`}
                        >
                          <span className="daynum">{Number(ymd.slice(-2))}</span>
                          {(ops > 0 || obs > 0) && (
                            <div className="cal-chip-stack">
                              {ops > 0 && (
                                <span className="cal-chip">{ops} Ops.</span>
                              )}
                              {obs > 0 && (
                                <span className="cal-chip">{obs} Obs.</span>
                              )}
                            </div>
                          )}
                          {total > 0 && (
                            <span className="cal-count">{total}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </div>

        <div className="cal-footer">
          <button className="btn" onClick={onClose}>
            <FiX /> Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ================== COMPONENTE PRINCIPAL ================== */
export default function Operaciones() {
  const location = useLocation();
  const navigate = useNavigate(); // para ir a operacionesDia
  useInjectStyles();

  const { deposito, setDeposito, isLogistica, isAdmin, isFinanzas } = useUserId();
  const depositoFromRoute = readDepositoFromSearch(location.search);
  const depositoActual = resolveDepositoRoute({
    search: location.search,
    fallback: deposito,
  });
  const depositoLabel = depositoActual === "binder2" ? "binder 2" : "binder 1";

  useEffect(() => {
    if (depositoFromRoute && depositoFromRoute !== deposito) {
      setDeposito(depositoFromRoute);
    }
  }, [depositoFromRoute, deposito, setDeposito]);

  const [fecha, setFecha] = useState(HOY);
  const today = useOperationalDay();
  const [loading, setLoading] = useState(false);

  const [weekDays, setWeekDays] = useState([]);
  const [weekMap, setWeekMap] = useState({});
  const [todayActivityState, setTodayActivityState] = useState({ key: "", activities: [], loading: true, error: "" });
  const todayActivityRequest = useRef(0);
  const todayActivityKey = `${depositoActual}:${today}`;
  const todayActivityView = todayActivityState.key === todayActivityKey ? todayActivityState
    : { activities: [], loading: true, error: "" };
  const [colWidths, setColWidths] = useState({
    label: BOARD_LABEL_COL_DEFAULT,
    day: BOARD_DAY_COL_DEFAULT,
  });
  const [rowHeights, setRowHeights] = useState({
    default: BOARD_ROW_HEIGHT_DEFAULT,
  });
  const gridRef = useRef(null);
  const gridContentRef = useRef(null);
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const sideScrollRef = useRef(null);
  const [gridScrollW, setGridScrollW] = useState(0);
  const [gridClientW, setGridClientW] = useState(0);
  const [gridScrollH, setGridScrollH] = useState(0);
  const [gridClientH, setGridClientH] = useState(0);
  const resizeMetaRef = useRef(null);
  const rowResizeMetaRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const [hoverLogo, setHoverLogo] = useState(null);
  const canCreate = isAdmin;
  const toggleObs = async ({ fechaIso, obsId, completada }) => {
  try {
    await apiOps.put(
      OPS.EDITAR_OBS(fechaIso, obsId),
      { completada, deposito: depositoActual } // deposito por si tu backend lo usa desde req
    );
    notifySuccess(completada ? "Observación completada." : "Observación marcada como pendiente.");
    if (fechaIso === today) {
      setTodayActivityState((prev) => prev.key !== todayActivityKey ? prev : { ...prev,
        activities: prev.activities.map((activity) => activity._id === obsId ? { ...activity, completada } : activity) });
    }

    // ✅ update optimista local (así no “parpadea”)
    setWeekMap((prev) => {
      const day = prev[fechaIso];
      if (!day) return prev;

      const nextObs = (day.observaciones_anotaciones || []).map((o) =>
        o._id === obsId ? { ...o, completada } : o
      );

      return { ...prev, [fechaIso]: { ...day, observaciones_anotaciones: nextObs } };
    });
  } catch (e) {
    console.error(e);
    alert("No se pudo actualizar la observación.");
  }
};

  const canEditOperacion = useCallback(
    (op) => {
      if (!op) return false;
      if (isAdmin) return true;
      if (isFinanzas) return op.tipo === "Egreso";
      return false;
    },
    [isAdmin, isFinanzas]
  );

  const canToggleCielFinanzas = useCallback(
    (op) => {
      if (!op || op.tipo !== "Egreso") return false;
      const tipoOp = String(op.tipoOperacion || "")
        .toLowerCase()
        .replace(/\s+/g, "");
      const esEgresoFiscal =
        tipoOp === "fiscalimpo" || tipoOp === "fiscalexpo";
      if (!esEgresoFiscal) return false;
      return isAdmin || isFinanzas;
    },
    [isAdmin, isFinanzas]
  );

  const canOpenCamion = useCallback(
    (op) => canEditOperacion(op) || canToggleCielFinanzas(op),
    [canEditOperacion, canToggleCielFinanzas]
  );

  const canEditLogos = useCallback(
    (op) => {
      if (!op) return false;
      return isAdmin;
    },
    [isAdmin]
  );

  // calendario mensual
  const [calOpen, setCalOpen] = useState(false);
  const [monthWeeks, setMonthWeeks] = useState([]);
  const [monthCounts, setMonthCounts] = useState(new Map());
  const [monthOpsCounts, setMonthOpsCounts] = useState(new Map());
  const [monthObsCounts, setMonthObsCounts] = useState(new Map());
  const [monthPendingSet, setMonthPendingSet] = useState(new Set());
  const [calMode, setCalMode] = useState("normal");

  const handleResizeMove = useCallback(
    (event) => {
      const meta = resizeMetaRef.current;
      if (!meta) return;
      const delta = event.clientX - meta.startX;
      const minWidth =
        meta.key === "label" ? BOARD_LABEL_COL_MIN : BOARD_DAY_COL_MIN;
      const next = Math.max(
        minWidth,
        Math.min(BOARD_COL_MAX, meta.startW + delta)
      );
      setColWidths((prev) => ({ ...prev, [meta.key === "label" ? "label" : "day"]: next }));
    },
    [setColWidths]
  );

  const stopResize = useCallback(() => {
    if (!resizeMetaRef.current) return;
    resizeMetaRef.current = null;
    window.removeEventListener("mousemove", handleResizeMove);
    window.removeEventListener("mouseup", stopResize);
  }, [handleResizeMove]);

  const startResize = (key, event) => {
    event.preventDefault();
    const base = event.currentTarget.parentElement?.getBoundingClientRect().width ||
      (key === "label" ? colWidths.label : colWidths.day);
    resizeMetaRef.current = {
      key,
      startX: event.clientX,
      startW: base,
    };
    window.addEventListener("mousemove", handleResizeMove);
    window.addEventListener("mouseup", stopResize);
  };

  useEffect(() => () => stopResize(), [stopResize]);

  const handleRowResizeMove = useCallback((event) => {
    const meta = rowResizeMetaRef.current;
    if (!meta) return;
    const delta = event.clientY - meta.startY;
    const next = Math.max(BOARD_ROW_HEIGHT_MIN, meta.startH + delta);
    setRowHeights({ default: next });
  }, []);

  const stopRowResize = useCallback(() => {
    if (!rowResizeMetaRef.current) return;
    rowResizeMetaRef.current = null;
    window.removeEventListener("mousemove", handleRowResizeMove);
    window.removeEventListener("mouseup", stopRowResize);
  }, [handleRowResizeMove]);

  const startRowResize = (key, event) => {
    event.preventDefault();
    const base = event.currentTarget.parentElement?.getBoundingClientRect().height ||
      rowHeights.default || BOARD_ROW_HEIGHT_DEFAULT;
    rowResizeMetaRef.current = {
      key,
      startY: event.clientY,
      startH: base,
    };
    window.addEventListener("mousemove", handleRowResizeMove);
    window.addEventListener("mouseup", stopRowResize);
  };

  useEffect(() => () => stopRowResize(), [stopRowResize]);

  // actualizaciÃ³n rÃ¡pida solo de logo (estado viene del logo)
  const quickActualizarCamion = async ({
    fechaIso,
    opId,
    camionIndex,
    nuevoLogo,
  }) => {
    try {
      const ops = weekMap[fechaIso]?.Operacion || [];
      const orig = ops.find((o) => o._id === opId);
      if (!orig) return;
      const op = normalizarCamiones({ ...orig, fecha: fechaIso });
      if (!canEditOperacion(op) || !canEditLogos(op)) return;
      const wantsLogo = nuevoLogo !== undefined;
      if (!wantsLogo) return;

      let camiones = Array.isArray(op.camiones) ? [...op.camiones] : [];
      const idx = camiones.findIndex((c) => c.index === camionIndex);
      if (idx >= 0) {
        camiones[idx] = {
          ...camiones[idx],
          dibujo: nuevoLogo != null && nuevoLogo !== "" ? nuevoLogo : "",
        };
      } else {
        camiones.push({
          index: camionIndex,
          dibujo: nuevoLogo != null && nuevoLogo !== "" ? nuevoLogo : "",
        });
      }

      const payload = {
        ...op,
        camiones,
        deposito: depositoActual,
      };
      await apiOps.put(OPS.EDITAR_OP(fechaIso, op._id), payload);
      notifySuccess("Estado del camión actualizado.");
      fetchData();
    } catch (err) {
      console.error(err);
      alert("No se pudo actualizar rÃ¡pido el camiÃ³n.");
    }
  };

  const quickActualizarControlOperacion = async ({ fechaIso, opId, field, value }) => {
    const op = (weekMap[fechaIso]?.Operacion || []).find((item) => String(item._id) === String(opId));
    if (!canToggleCielFinanzas(op) || !["siel", "facturacion"].includes(field)) return;
    try {
      await apiOps.put(OPS.EDITAR_OP(fechaIso, opId), { [field]: !!value, deposito: depositoActual });
      notifySuccess(`${field === "siel" ? "SIEL actualizado" : "Facturación actualizada"} correctamente.`);
      await fetchData();
    } catch (error) {
      console.error(error);
      alert("No se pudo actualizar el control de la operación.");
    }
  };

  const [crearOpen, setCrearOpen] = useState(false);
  const [fechaCrear, setFechaCrear] = useState(null);
  const [tipoCrear, setTipoCrear] = useState("");
  const [movCrear, setMovCrear] = useState("");
  const cantidadCamionesCrearRef = useRef(null);
  const [formOp, setFormOp] = useState({
    nombreCliente: "",
    cliente: null,
    dibujo: "",
    cantidadCamiones: 1,
    dap: false,
    siel: false,
    facturacion: false,
    Contenedores: [],
  });

  const [formObs, setFormObs] = useState({ observacion: "" });

  const [contenedorCrear, setContenedorCrear] = useState({
    nombre: "",
  });

  // ediciÃ³n
  const [editOp, setEditOp] = useState(null);
  const [camionActivo, setCamionActivo] = useState(0);
  const [contenedorDraft, setContenedorDraft] = useState({
    nombre: "",
  });
  const [contenedorEditando, setContenedorEditando] = useState({
    index: null,
    nombre: "",
  });
  const puedeEditarOpActual = editOp ? canEditOperacion(editOp) : false;
  const puedeEditarLogoActual = editOp ? canEditLogos(editOp) : false;
  const puedeEditarCielFinanzas =
    editOp && canToggleCielFinanzas(editOp);

  const getTextoObservacionCamion = (item) => {
    const nombre = String(item?.nombre || "").trim();
    const obs = String(item?.obs || "").trim();
    if (nombre && obs) return `${nombre} - ${obs}`;
    return nombre || obs;
  };

  // logos
  const [logos, setLogos] = useState([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      const list = await loadLogoList();
      if (!cancel) setLogos(list);
    })();
    return () => {
      cancel = true;
    };
  }, []);

  // abrir modal mensual desde ?cal=1 (bloqueado para Visualizador Logistica)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasCal = params.get("cal") === "1";

    if (isLogistica && hasCal) {
      params.delete("cal");
      const qs = params.toString();
      const next = qs ? `?${qs}` : "";
      if (location.search !== next) {
        navigate(`${location.pathname}${next}`, { replace: true });
      }
      return;
    }

    if (hasCal) {
      setCalMode("normal");
      setCalOpen(true);
      // limpiamos el query param para que no reabra cada render
      params.delete("cal");
      const qs = params.toString();
      const next = qs ? `?${qs}` : "";
      if (location.search !== next) {
        navigate(`${location.pathname}${next}`, { replace: true });
      }
    }
  }, [isLogistica, location.pathname, location.search, navigate]);

  // Sincronizar scroll horizontal y medir ancho del grid semanal
  useLayoutEffect(() => {
    const viewport = gridRef.current;
    const content = gridContentRef.current;
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    const side = sideScrollRef.current;

    if (!viewport) {
      setGridScrollW(0);
      setGridClientW(0);
      setGridScrollH(0);
      setGridClientH(0);
      return undefined;
    }

    const syncAxis = (source, targets, axis) => {
      if (!source) return;
      const key = axis === "y" ? "scrollTop" : "scrollLeft";
      const nextValue = source[key];
      targets.forEach((target) => {
        if (target && target[key] !== nextValue) {
          target[key] = nextValue;
        }
      });
    };

    const syncFromTop = () => syncAxis(top, [viewport, bottom], "x");
    const syncFromBottom = () => syncAxis(bottom, [viewport, top], "x");
    const syncFromSide = () => syncAxis(side, [viewport], "y");
    const syncFromViewport = () => {
      syncAxis(viewport, [top, bottom], "x");
      syncAxis(viewport, [side], "y");
    };

    const measure = () => {
      const nextScrollW = viewport.scrollWidth || content?.scrollWidth || 0;
      const nextClientW = viewport.clientWidth || 0;
      const nextScrollH = viewport.scrollHeight || content?.scrollHeight || 0;
      const nextClientH = viewport.clientHeight || 0;

      setGridScrollW(nextScrollW);
      setGridClientW(nextClientW);
      setGridScrollH(nextScrollH);
      setGridClientH(nextClientH);

      if (top) top.scrollLeft = viewport.scrollLeft;
      if (bottom) bottom.scrollLeft = viewport.scrollLeft;
      if (side) side.scrollTop = viewport.scrollTop;
    };

    let measureFrame = 0;
    const queueMeasure = () => {
      if (measureFrame) cancelAnimationFrame(measureFrame);
      measureFrame = window.requestAnimationFrame(() => {
        measureFrame = 0;
        measure();
      });
    };

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => queueMeasure())
        : null;

    top?.addEventListener("scroll", syncFromTop);
    bottom?.addEventListener("scroll", syncFromBottom);
    side?.addEventListener("scroll", syncFromSide);
    viewport.addEventListener("scroll", syncFromViewport);
    window.addEventListener("resize", queueMeasure);
    resizeObserver?.observe(viewport);
    if (content) {
      resizeObserver?.observe(content);
    }

    measure();
    queueMeasure();

    return () => {
      if (measureFrame) cancelAnimationFrame(measureFrame);
      top?.removeEventListener("scroll", syncFromTop);
      bottom?.removeEventListener("scroll", syncFromBottom);
      side?.removeEventListener("scroll", syncFromSide);
      viewport.removeEventListener("scroll", syncFromViewport);
      window.removeEventListener("resize", queueMeasure);
      resizeObserver?.disconnect();
    };
  }, [weekDays, weekMap, colWidths, rowHeights]);

  /* ==== fetchers ==== */
  const fetchDia = async (f) => {
    const { data } = await apiOps.get(OPS.LISTAR, {
      params: { fecha: f, deposito: depositoActual },
    });
    const registro = Array.isArray(data) ? data[0] || {} : data || {};
    return {
      Operacion: Array.isArray(registro.Operacion)
        ? registro.Operacion
        : [],
      observaciones_anotaciones: Array.isArray(
        registro.observaciones_anotaciones
      )
        ? registro.observaciones_anotaciones
        : [],
    };
  };

  const fetchSemana = async (ymdBase) => {
    const { from, to } = rangeWeek(ymdBase);
    const dias = daysBetween(from, to);
    setWeekDays(dias);

    const map = {};
    const batch = await Promise.allSettled(dias.map((d) => fetchDia(d)));
    dias.forEach((d, idx) => {
      const r = batch[idx];
      if (r.status === "fulfilled" && r.value) map[d] = r.value;
      else map[d] = { Operacion: [], observaciones_anotaciones: [] };
    });
    setWeekMap(map);

    const { weeks } = buildMonthMatrix(ymdBase);
    const monthPrefix = ymdBase.slice(0, 7);
    const filtered = weeks
      .filter((week) => week.some((day) => day.slice(0, 7) === monthPrefix))
      .map((week, idx) => ({
        index: idx + 1,
        week,
        from: week[0],
        to: week[6],
      }));
    setMonthWeeks(filtered);
  };

  const fetchMonthMarks = async (ym) => {
    const { from, to } = rangeMonth(ym + "-01");
    const counts = new Map();
    const opsCounts = new Map();
    const obsCounts = new Map();
    const pendingSet = new Set();

    try {
      const { data } = await apiOps.get(OPS.STATS_RANGO, {
        params: { desde: from, hasta: to, deposito: depositoActual },
      });
      const rows = Array.isArray(data) ? data : [];
      rows.forEach((r) => {
        const ymd = String(r.fecha).slice(0, 10);
        const total = Number(r.total || 0);
        const ops = Number(r.ops || 0);
        const obs = Number(r.obs || 0);
        const pendientes = Number(r.pendientes || 0);

        if (total > 0) counts.set(ymd, total);
        if (ops > 0) opsCounts.set(ymd, ops);
        if (obs > 0) obsCounts.set(ymd, obs);
        if (pendientes > 0) pendingSet.add(ymd);
      });
    } catch {}

    setMonthCounts(counts);
    setMonthOpsCounts(opsCounts);
    setMonthObsCounts(obsCounts);
    setMonthPendingSet(pendingSet);
  };

  const fetchTodayActivities = async () => {
    const request = ++todayActivityRequest.current;
    setTodayActivityState((prev) => ({ key: todayActivityKey,
      activities: prev.key === todayActivityKey ? prev.activities : [], loading: true, error: "" }));
    try {
      const day = await fetchDia(today);
      if (request === todayActivityRequest.current) setTodayActivityState({ key: todayActivityKey,
        activities: day.observaciones_anotaciones.filter((note) => note.programacionId), loading: false, error: "" });
    } catch {
      if (request === todayActivityRequest.current) setTodayActivityState({ key: todayActivityKey, activities: [], loading: false,
        error: "No se pudieron cargar las actividades de hoy. Usá Actualizar para reintentar." });
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchSemana(fecha), fetchMonthMarks(fecha.slice(0, 7)), fetchTodayActivities()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha, depositoActual, today]);

  /* ==== helpers semana ==== */
  useEffect(() => {
    if (calOpen) fetchMonthMarks(fecha.slice(0, 7));
    // Refresh server-derived marks whenever the monthly view is reopened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calOpen, fecha, depositoActual]);

  useEffect(() => {
    setEditOp(null);
    setHoverLogo(null);
    setCrearOpen(false);
  }, [fecha]);

  const stepFecha = (dir) => {
    const base = ymdToDateUTC(fecha);
    base.setUTCDate(base.getUTCDate() + (dir === "prev" ? -7 : 7));
    setFecha(fmt(base));
  };

  const etiquetaFecha = () => {
    if (!weekDays.length) return "Semana";
    const activeWeek = monthWeeks.find((week) => week.week[0] === weekDays[0]);
    const fmtShort = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
    if (activeWeek) {
      return `Semana ${activeWeek.index}: ${fmtShort(
        activeWeek.from
      )} a ${fmtShort(activeWeek.to)}`;
    }
    return `Semana: ${fmtShort(weekDays[0])} a ${fmtShort(weekDays[6])}`;
  };

  /* ==== Crear operaciÃ³n ==== */
  const abrirCrear = (tipo, mov, ymd) => {
    if (!canCreate) return;
    setTipoCrear(tipo);
    setMovCrear(mov);
    setFechaCrear(ymd || fecha);
    setFormOp({
      nombreCliente: "",
      cliente: null,
      dibujo: "",
      cantidadCamiones: 1,
      dap: false,
      siel: false,
      facturacion: false,
      Contenedores: [],
    });
    setContenedorCrear({ nombre: "" });
    setFormObs({ observacion: "" });
    setCrearOpen(true);
  };

  const agregarContenedorCrear = () => {
    if (!canCreate) return;
    const observacion = contenedorCrear.nombre.trim();
    if (!observacion) return;

    const nuevos = [{ nombre: observacion, obs: "" }];

    setFormOp((prev) => ({
      ...prev,
      Contenedores: [...(prev.Contenedores || []), ...nuevos],
    }));

    setContenedorCrear({ nombre: "" });
  };

  const crear = async () => {
    if (!canCreate) return;
    const fechaDestino = fechaCrear || fecha;

    try {
      if (movCrear === "Observacion") {
        const payload = {
          ...formObs,
          ambito: tipoCrear,
          fecha: fechaDestino,
          deposito: depositoActual,
        };
        await apiOps.post(OPS.CREAR_OBS(fechaDestino), payload);
      } else {
        const nCam = Number(formOp.cantidadCamiones);
        if (!Number.isSafeInteger(nCam) || nCam < 1) {
          alert("Ingresá una cantidad de camiones entera, mayor o igual a 1.");
          cantidadCamionesCrearRef.current?.focus();
          return;
        }
        const plantilla = Array.isArray(formOp.Contenedores)
          ? formOp.Contenedores
          : [];

        const contenedoresExpandidos = [];
        for (let idx = 0; idx < nCam; idx++) {
          plantilla.forEach((c) => {
            contenedoresExpandidos.push({
              ...c,
              camionIndex: idx,
            });
          });
        }

        const payload = {
          ...formOp,
          dibujo: formOp.dibujo || "",
          nombreCliente:
            formOp?.cliente?.nombreCliente || formOp.nombreCliente || "",
          tipoOperacion: tipoCrear,
          tipo: movCrear,
          fecha: fechaDestino,
          Usuario: "admin",
          deposito: depositoActual,
          cantidadCamiones: nCam,
          Contenedores: contenedoresExpandidos,
        };

        await apiOps.post(OPS.CREAR_OP(fechaDestino), payload);
      }

      notifySuccess(movCrear === "Observacion" ? "Observación creada correctamente." : "Operación creada correctamente.");
      setCrearOpen(false);
      setFechaCrear(null);
      fetchData();
    } catch (e) {
      console.error(e);
      alert("No pude crear el registro.");
    }
  };

  /* ==== ediciÃ³n de contenedores por camiÃ³n ==== */
  const agregarContenedorCamion = () => {
    const observacion = contenedorDraft.nombre.trim();
    if (!editOp || !canEditOperacion(editOp) || !observacion) return;
    const nuevo = {
      nombre: observacion,
      obs: "",
      estado: "Completar",
      camionIndex: camionActivo,
    };
    setEditOp((op) => ({
      ...op,
      Contenedores: [...(op.Contenedores || []), nuevo],
    }));
    setContenedorDraft({ nombre: "" });
    setContenedorEditando({ index: null, nombre: "" });
  };

  const eliminarContenedor = (id) => {
    if (!editOp || !canEditOperacion(editOp)) return;
    setContenedorEditando((prev) => {
      if (prev.index === id) return { index: null, nombre: "" };
      if (prev.index !== null && prev.index > id) {
        return { ...prev, index: prev.index - 1 };
      }
      return prev;
    });
    setEditOp((op) => ({
      ...op,
      Contenedores: (op.Contenedores || []).filter((_, idx) => idx !== id),
    }));
  };

  const editarContenedor = (idx) => {
    if (!editOp || !canEditOperacion(editOp)) return;
    const actual = (editOp.Contenedores || [])[idx];
    setContenedorEditando({
      index: idx,
      nombre: getTextoObservacionCamion(actual),
    });
  };

  const guardarEdicionContenedor = (idx) => {
    const observacion = contenedorEditando.nombre.trim();
    if (
      !editOp ||
      !canEditOperacion(editOp) ||
      contenedorEditando.index !== idx ||
      !observacion
    ) {
      return;
    }
    setEditOp((op) => {
      const arr = [...(op.Contenedores || [])];
      arr[idx] = {
        ...(arr[idx] || {}),
        nombre: observacion,
        obs: "",
      };
      return { ...op, Contenedores: arr };
    });
    setContenedorEditando({ index: null, nombre: "" });
  };

  const cambiarEstadoContenedor = (idx, estado) => {
    if (!editOp || !canEditOperacion(editOp)) return;
    setEditOp((op) => {
      const arr = [...(op.Contenedores || [])];
      arr[idx] = { ...(arr[idx] || {}), estado };
      return { ...op, Contenedores: arr };
    });
  };

  const guardarCamion = async () => {
    if (!editOp || !canEditOperacion(editOp)) return;
    let opActualizada = editOp;

    if (contenedorEditando.index !== null) {
      const observacionPendiente = contenedorEditando.nombre.trim();
      if (!observacionPendiente) {
        alert("La observacion no puede estar vacia.");
        return;
      }

      opActualizada = {
        ...editOp,
        Contenedores: (editOp.Contenedores || []).map((item, idx) =>
          idx === contenedorEditando.index
            ? { ...item, nombre: observacionPendiente, obs: "" }
            : item
        ),
      };

      setEditOp(opActualizada);
      setContenedorEditando({ index: null, nombre: "" });
    }

    try {
      const payload = {
        ...opActualizada,
        dibujo: opActualizada.dibujo || "",
        nombreCliente: opActualizada.nombreCliente || "",
        deposito: depositoActual,
      };
      await apiOps.put(
        OPS.EDITAR_OP(opActualizada.fecha.slice(0, 10) || fecha, opActualizada._id),
        payload
      );
      notifySuccess(`Camión ${camionActivo + 1} actualizado correctamente.`);
      setEditOp(null);
      fetchData();
    } catch {
      alert("No pude guardar los cambios.");
    }
  };

  const borrarOp = async () => {
    if (!editOp || !isAdmin) return;
    if (!window.confirm("\u00bfEliminar la operaci\u00f3n completa?")) return;
    try {
      await apiOps.delete(
        OPS.BORRAR_OP(editOp.fecha.slice(0, 10) || fecha, editOp._id),
        { params: { deposito: depositoActual } }
      );
      notifySuccess("Operación eliminada correctamente.");
      setEditOp(null);
      fetchData();
    } catch (error) {
      console.error("Eliminar operación:", error);
      alert(operationDeleteError(error, "la operación"));
    }
  };

  const cerrarTodosContenedoresCamion = () => {
    if (!editOp || !canEditOperacion(editOp)) return;
    setEditOp((op) => ({
      ...op,
      Contenedores: (op.Contenedores || []).map((c) =>
        c.camionIndex === camionActivo ? { ...c, estado: "Finalizado" } : c
      ),
    }));
  };

  const posponerOperacion = async () => {
    if (!editOp || !isAdmin) return;
    if (
      !window.confirm(
        "\u00bfEst\u00e1s seguro de pasar para ma\u00f1ana solo las observaciones pendientes?"
      )
    )
      return;
    try {
      await apiOps.post(
        OPS.POSPONER_OP(editOp.fecha.slice(0, 10) || fecha, editOp._id),
        { deposito: depositoActual }
      );
      notifySuccess("Traslado al día siguiente guardado correctamente.");
      setEditOp(null);
      fetchData();
    } catch {
      alert(
        "No pude posponer la operaci\u00f3n. Verific\u00e1 el endpoint en el servidor."
      );
    }
  };

  const normalizarCamiones = (op) => {
    const cantidad = Number(op.cantidadCamiones) || 0;
    const camionesLen = Array.isArray(op.camiones) ? op.camiones.length : 0;
    const nCam = Math.max(1, cantidad, camionesLen);
    let cams = Array.isArray(op.camiones) ? [...op.camiones] : [];

    if (cams.length < nCam) {
      for (let i = cams.length; i < nCam; i++) {
        cams.push({
          index: i,
          dibujo: op.dibujo || "",
        });
      }
    }

    cams = cams.slice(0, nCam).map((c, i) => ({
      index: typeof c.index === "number" ? c.index : i,
      dibujo: c.dibujo || op.dibujo || "",
      pendiente: !!c.pendiente,
      finalizado: !!c.finalizado,
      ciel: !!c.ciel,
      finanzas: !!c.finanzas,
    }));

    return {
      ...op,
      cantidadCamiones: nCam,
      camiones: cams,
    };
  };

  const openCamion = (op, camionIndex, fechaIso) => {
    if (!canOpenCamion({ ...op, fecha: fechaIso || op.fecha || fecha })) return;
    setHoverLogo(null);
    const opNorm = normalizarCamiones(op);
    setContenedorDraft({ nombre: "" });
    setContenedorEditando({ index: null, nombre: "" });
    setEditOp({
      ...opNorm,
      fecha: fechaIso || opNorm.fecha || fecha,
    });
    setCamionActivo(camionIndex);
  };

  const clearHoverTimeout = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const scheduleHideHover = () => {
    clearHoverTimeout();
    hoverTimerRef.current = setTimeout(() => setHoverLogo(null), 180);
  };

  const showHover = (event, op, camionIndex, logoVal, fechaIso) => {
    if (!canOpenCamion({ ...op, fecha: fechaIso }) && !canEditLogos({ ...op, fecha: fechaIso })) return;
    clearHoverTimeout();
    const rect = event.currentTarget.getBoundingClientRect();
    const opNorm = normalizarCamiones({ ...op, fecha: fechaIso || op.fecha || fecha });
    const camion = getCamionByIndex(opNorm, camionIndex);

    const contenedores = (opNorm.Contenedores || [])
      .map((c, idx) => ({ ...c, __idx: idx }))
      .filter((c) => (c.camionIndex || 0) === camionIndex);

    const estado = estadoCamionPorLogo(logoVal || opNorm.dibujo);

    setHoverLogo({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8,
      opId: opNorm._id,
      camionIndex,
      logo: logoVal || "",
      estado,
      cliente: opNorm.nombreCliente || "Sin cliente",
      contenedores,
      fechaIso,
      op: opNorm,
      ciel: !!camion?.ciel,
      siel: getOperationSiel(opNorm),
      facturacion: getOperacionFacturacion(opNorm),
      canToggleCielFinanzas: canToggleCielFinanzas(opNorm),
    });
  };

  const handleHoverLogo = async (val) => {
    if (!hoverLogo?.op || !canEditLogos(hoverLogo.op)) return;
    setHoverLogo((h) =>
      h ? { ...h, logo: val, estado: estadoCamionPorLogo(val) } : h
    );
    await quickActualizarCamion({
      fechaIso: hoverLogo.fechaIso,
      opId: hoverLogo.opId,
      camionIndex: hoverLogo.camionIndex,
      nuevoLogo: val,
    });
  };

  const handleHoverControl = async (field, value) => {
    if (!hoverLogo?.canToggleCielFinanzas) return;
    setHoverLogo((current) => current ? { ...current, [field]: !!value,
      op: withOperationControl(current.op, field, value) } : current);
    await quickActualizarControlOperacion({
      fechaIso: hoverLogo.fechaIso,
      opId: hoverLogo.opId, field, value,
    });
  };

  const renderWeekPanels = () => {
    const days = weekDays
      .filter((iso) => {
        const d = new Date(iso + "T00:00:00");
        return d.getDay() !== 0; // sin domingo
      })
      .map((iso) => ({
        iso,
        label: formatDayLabel(iso),
        data: weekMap[iso] || { Operacion: [], observaciones_anotaciones: [] },
      }));
    const pendingDaySet = new Set(days.filter((day) =>
      day.iso >= today && (day.data.Operacion || []).some((op) => getOperationDayState(op, day.iso, estadoCamionPorLogo).hasPending)
    ).map((day) => day.iso));
  const renderItems = (row, day) => {
  // ✅ OBSERVACIONES
  if (row.kind === "obs") {
    const ambito = row.ambito;
    const lista = (day.data.observaciones_anotaciones || []).filter(
      (o) => !o.programacionId && String(o.ambito || "").trim().toLowerCase() === ambito
    );

    if (!lista.length) return <span className="empty">Sin observaciones</span>;

    return (
      <div className="weekly-observation-groups">
            <section className="weekly-observation-group"
              aria-label={ambito === "fiscal" ? "Observaciones fiscales" : "Observaciones nacionales"}>
              {lista.map((o) => (
                <label
                  key={o._id}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    padding: "5px 8px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,.12)",
                    background: "rgba(255,255,255,.05)",
                    cursor: "pointer",
                  }}
                  title={o.observacion || ""}
                >
                  <input
                    type="checkbox"
                    checked={!!o.completada}
                    onChange={(e) =>
                      toggleObs({
                        fechaIso: day.iso,
                        obsId: o._id,
                        completada: e.target.checked,
                      })
                    }
                    style={{ marginTop: 2 }}
                  />
                  <span
                    style={{
                      fontSize: 11.5,
                      color: o.completada ? "#94a3b8" : "#effff7",
                      textDecoration: "none",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {o.observacion || "(sin texto)"}
                  </span>
                </label>
              ))}
            </section>
      </div>
    );
  }

  // ✅ OPERACIONES
  const ops = (day.data.Operacion || []).filter(
    (op) => op.tipoOperacion === row.tipoOperacion && op.tipo === row.tipo
  );

  if (!ops.length) return <span className="empty">Sin Operacion</span>;

  return (
    <div className="weekly-cell-content">
      <div className="weekly-cell-records">
      <OperationDapOrder operations={ops}>{(op) => {
        // Si es solo comentario
        if (op.soloComentario) {
          return (
            <div key={op._id} className="board-item comentario">
              <div className="board-item-header">
                <div className="board-item-title">
                  <span className="weekly-operation-title">
                    {op.nombreCliente || "Comentario"}
                  </span>
                </div>
              </div>
              <div className="ops-comment-text">{op.comentario || ""}</div>
            </div>
          );
        }

        // ====== operación normal con camiones ======
        const opNorm = normalizarCamiones({ ...op, fecha: day.iso });
        const cant = Math.max(
          1,
          Number(opNorm.cantidadCamiones || (opNorm.camiones || []).length || 1)
        );

        const tienePendientes = getOperationDayState(op, day.iso, estadoCamionPorLogo).hasPending;

        const logosToShow = Array.from({ length: cant }).map((_, idxLogo) => {
          const cam =
            (opNorm.camiones || []).find((c) => c.index === idxLogo) || null;
          return cam?.dibujo || opNorm.dibujo || "";
        });

        const transfers = getOperationTransfers(op, day.iso);

        return (
          <div
            key={op._id}
            className={
              "board-item weekly-compact" +
              (tienePendientes ? " pending" : "") +
              (transfers.incoming ? " operation-has-incoming-transfer" : "")
            }
          >
            <OperationTransferIndicator direction="incoming" label={transfers.incoming}
              className="weekly-transfer-indicator" />
            <div className="weekly-row">
              <div className="board-item-title">
                <span className="weekly-operation-title">
                  {op.nombreCliente || "Sin cliente"}
                </span>
                <OperationTransferIndicator direction="outgoing" label={transfers.outgoing}
                  className="weekly-transfer-indicator" />
              </div>
              <div className="weekly-logos" style={{ "--truck-columns": Math.min(4, cant) }}>
                {logosToShow.map((imgName, idxLogo) => {
                  const imgSrc = imgName ? logoUrl(imgName, op.dap) : null;
                  const puedeAbrirCamion = canOpenCamion(opNorm);
                  return (
                    <div
                      key={idxLogo}
                      className={"weekly-logo" + (puedeAbrirCamion ? " editable" : "")}
                      onMouseEnter={
                        isAdmin
                          ? (e) =>
                              showHover(
                                e,
                                opNorm,
                                idxLogo,
                                imgName || opNorm.dibujo || "",
                                day.iso
                              )
                          : undefined
                      }
                      onMouseLeave={isAdmin ? scheduleHideHover : undefined}
                      onClick={
                        puedeAbrirCamion
                          ? () => openCamion(opNorm, idxLogo, day.iso)
                          : undefined
                      }
                      title={
                        puedeAbrirCamion
                          ? `Editar cami\u00f3n - Cami\u00f3n ${idxLogo + 1}`
                          : ""
                      }
                    >
                      {imgSrc ? (
                        <img src={imgSrc} alt="logo" />
                      ) : (
                        <span className="weekly-placeholder">
                          {(op.nombreCliente || "SC").slice(0, 2)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      }}</OperationDapOrder>

      </div>
    </div>
  );
};


    const colTemplate = [
      `${colWidths.label || BOARD_LABEL_COL_DEFAULT}px`,
      ...days.map(() => `${colWidths.day || BOARD_DAY_COL_DEFAULT}px`),
    ]
      .join(" ");
    const showHorizontalGridNav = gridScrollW > gridClientW + 1;
    const showVerticalGridNav = gridScrollH > gridClientH + 1;

    return (
      <section className="calendar-board">
        <div className="board-head">
          <div className="board-head-copy">
            <p className="muted">Semana seleccionada</p>
            <h2>{etiquetaFecha()}</h2>
          </div>
        {!!monthWeeks.length && (
          <div className="weekly-week-picker">
            <div
              className="week-tabs"
              role="tablist"
              aria-label="Semanas del mes"
            >
              {monthWeeks.map((week) => {
                const active = week.week[0] === weekDays[0];
                return (
                  <button
                    key={week.index}
                    type="button"
                    className={"week-tab" + (active ? " active" : "")}
                    onClick={() => setFecha(week.week[0])}
                    aria-pressed={active}
                  >
                    <span>Semana {week.index}</span>
                    <small>
                      {formatShort(week.from)} - {formatShort(week.to)}
                    </small>
                  </button>
                );
              })}
            </div>
          </div>
        )}
          <div className="board-actions">
            <button className="btn ghost" onClick={() => stepFecha("prev")}>
              <FiChevronLeft /> Semana anterior
            </button>
            <button className="btn ghost" onClick={() => stepFecha("next")}>
              Semana siguiente <FiChevronRight />
            </button>
          </div>
        </div>

        <div className="weekly-board-content">
        <div className="board-grid-shell">
          <div
            className="board-grid-scrollbar board-grid-scrollbar--top"
            ref={topScrollRef}
            style={{ display: showHorizontalGridNav ? "block" : "none", width: gridClientW || "100%" }}
            tabIndex={0}
            role="region"
            aria-label="Desplazamiento horizontal del calendario (superior)"
          >
            <div style={{ width: gridScrollW, height: 1 }} />
          </div>

          <div
            className={
              "board-grid-frame" + (showVerticalGridNav ? " has-vertical-nav" : "")
            }
          >
            <div
              className="board-grid-wrap"
              ref={gridRef}
              tabIndex={0}
              aria-label="Tablero semanal desplazable"
            >
              <div
                className="board-grid"
                style={{ gridTemplateColumns: colTemplate }}
                ref={gridContentRef}
              >
                {/* cabecera columna de categorÃ­as */}
                <div className="row-label-col">
                  {"Categor\u00eda"}
                  <span
                    className="col-resizer"
                    onMouseDown={(e) => startResize("label", e)}
                    title="Arrastrá para ajustar el ancho"
                    role="presentation"
                  />
                </div>

                {/* cabecera de dÃ­as */}
                {days.map((day) => (
                  <div
                    key={day.iso}
                    className={
                      "day-head" + (pendingDaySet.has(day.iso) ? " has-pending" : "")
                    }
                    title={
                      pendingDaySet.has(day.iso)
                        ? "Operaciones pendientes en este día"
                        : undefined
                    }
                  >
                    <span className="day-week">{day.label.split(" ")[0]}</span>
                    <span className="day-date">
                      {day.label.split(" ").slice(1).join(" ")}
                    </span>
                    <span
                      className="col-resizer"
                      onMouseDown={(e) => startResize(day.iso, e)}
                      title="Arrastrá para ajustar el ancho de todos los días"
                      role="presentation"
                    />
                  </div>
                ))}

                {/* filas por categorÃ­a */}
                {BOARD_ROWS.map((row) => (
                  <React.Fragment key={row.id}>
                    <div className="row-label" data-row={row.id}>
                      <span>{row.label}</span>
                      <span
                        className="row-resizer"
                        onMouseDown={(e) => startRowResize(row.id, e)}
                        title="Arrastrá para ajustar el alto de todas las celdas"
                        role="presentation"
                      />
                    </div>

                    {days.map((day) => (
                      <div
                        key={`${row.id}-${day.iso}`}
                        className="board-cell"
                        data-row={row.id}
                        data-date={day.iso}
                        style={{
                          minHeight:
                            rowHeights.default ||
                            undefined,
                        }}
                      >
                        <div className="cell-body">
                          {renderItems(row, day)}
                        </div>
                        <span className="col-resizer" role="presentation"
                          title="Arrastrá para ajustar el ancho de todos los días"
                          onMouseDown={(event) => startResize(day.iso, event)} />
                        <span className="row-resizer" role="presentation"
                          title="Arrastrá para ajustar el alto de todas las celdas"
                          onMouseDown={(event) => startRowResize(row.id, event)} />
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div
              className="board-grid-scrollbar-y"
              ref={sideScrollRef}
              style={{ display: showVerticalGridNav ? "block" : "none" }}
              tabIndex={0}
              role="region"
              aria-label="Desplazamiento vertical del calendario"
            >
              <div style={{ height: gridScrollH, width: 1 }} />
            </div>
          </div>

          <div
            className="board-grid-scrollbar board-grid-scrollbar--bottom"
            ref={bottomScrollRef}
            style={{ display: showHorizontalGridNav ? "block" : "none", width: gridClientW || "100%" }}
            tabIndex={0}
            role="region"
            aria-label="Desplazamiento horizontal del calendario (inferior)"
          >
            <div style={{ width: gridScrollW, height: 1 }} />
          </div>
        </div>
        <TodayScheduledActivities date={today} activities={todayActivityView.activities}
          loading={todayActivityView.loading} error={todayActivityView.error} onToggle={toggleObs} />
        </div>
      </section>
    );
  };


  /* ==== controles SIEL / facturaci\u00f3n ==== */

  const setOperacionControl = (field, value) => {
    if (!editOp || !canToggleCielFinanzas(editOp)) return;
    setEditOp((operation) => withOperationControl(operation, field, value));
  };
  const sielActivo = getOperationSiel(editOp);
  const facturacionActiva = getOperacionFacturacion(editOp);

  /* ==== render principal ==== */
  return (
    <div className="users-wrap calendar-wrap">
      <header className="users-header">
        <div className="title-stack">
          <p className="muted">Panel operativo</p>
          <h1>Calendario semanal</h1>
          <small>{depositoLabel}</small>

        </div>
        <div className="toolbar">
          <button type="button" className="btn ghost" onClick={fetchData} disabled={loading} aria-busy={loading}>
            <FiRefreshCw aria-hidden="true" />
            <span>{loading ? "Cargando..." : "Actualizar"}</span>
          </button>
        </div>
        <div className="toolbar-center">
          {!isLogistica && (
            <>
              <button
                className="btn ghost"
                onClick={() =>
                  navigate(`/operacionesdia?fecha=${fecha}&deposito=${depositoActual}`)
                }
              >
                <FiCalendar /> Diario
              </button>
              <button
                className="btn ghost"
                onClick={() => {
                  setCalMode("normal");
                  setCalOpen(true);
                }}
              >
                <FiCalendar /> Mensual
              </button>
              <button
                className="btn ghost"
                onClick={() => {
                  setCalMode("pendientes");
                  setCalOpen(true);
                }}
              >
                <FiClock /> Pendientes
              </button>
              <ScheduledActivities deposito={depositoActual} selectedDate={fecha} onChanged={fetchData} />
            </>
          )}
        </div>
      </header>

      {loading && <div className="panel empty">{"Cargando informaci\u00f3n..."}</div>}

      {renderWeekPanels()}

      {hoverLogo && (
        <div
          className="win98-pop"
          style={{
            left: hoverLogo.x,
            top: hoverLogo.y,
            transform: "translate(-50%, 0)",
          }}
          onMouseEnter={clearHoverTimeout}
          onMouseLeave={scheduleHideHover}
        >
          <h4>{`Cami\u00f3n ${hoverLogo.camionIndex + 1}`}</h4>
          <div style={{ fontSize: 12, marginBottom: 6, color: "#111827", opacity: 0.9 }}>
            {hoverLogo.cliente}
          </div>
          {hoverLogo.contenedores?.length ? (
            <div className="win98-list">
              {hoverLogo.contenedores.map((c) => (
                <div key={c.__idx} className="win98-row">
                  <span className="win98-name">
                    {getTextoObservacionCamion(c) || "Sin observaci\u00f3n"}
                  </span>
                  <span style={{ fontSize: 12 }}>{c.estado || "Sin estado"}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="win98-empty">Sin observaciones</div>
          )}
          <div className="win98-row">
            <label>Estado</label>
            <div style={{ fontSize: 12 }}>
              {hoverLogo.estado === "finalizado"
                ? "Finalizado"
                : hoverLogo.estado === "proceso"
                ? "En proceso"
                : "Pendiente"}
            </div>
          </div>
          <div className="win98-row">
            <label>Logo</label>
            <TruckLogoDropdown value={hoverLogo.logo} onChange={handleHoverLogo}
              logos={logos} movement={hoverLogo.op?.tipo} operationType={hoverLogo.op?.tipoOperacion} logoUrl={logoUrl} dap={hoverLogo.op?.dap} disabled={!hoverLogo.op || !canEditLogos(hoverLogo.op)} />
          </div>
          {hoverLogo.canToggleCielFinanzas && (
            <div className="win98-row" style={{ display: "block" }}>
              <label style={{ display: "block", marginBottom: 6 }}>
                {CIEL_SIEL_LABEL}
              </label>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}
                >
                  <input
                    type="checkbox"
                    checked={!!hoverLogo.siel}
                    onChange={(e) =>
                      handleHoverControl("siel", e.target.checked)
                    }
                  />
                  {SIEL_LABEL}
                </label>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}
                >
                  <input
                    type="checkbox"
                    checked={!!hoverLogo.facturacion}
                    onChange={(e) =>
                      handleHoverControl("facturacion", e.target.checked)
                    }
                  />
                  {FACTURACION_LABEL}
                </label>
              </div>
            </div>
          )}
          <div className="win98-actions">
            <button
              type="button"
              className="win98-btn"
              onClick={() => {
                const dayOps = weekMap[hoverLogo.fechaIso]?.Operacion || [];
                const op = dayOps.find((o) => o._id === hoverLogo.opId);
                if (op) openCamion(op, hoverLogo.camionIndex, hoverLogo.fechaIso);
              }}
            >
              Editar observaciones
            </button>
          </div>
        </div>
      )}

      {/* Drawer crear operación */}
      <Drawer
        open={crearOpen}
        onClose={() => {
          setCrearOpen(false);
          setFechaCrear(null);
        }}
        className="ops-modal"
      >
        <div className="drawer-header">
          <h3>
            {`Crear operaci\u00f3n ${tipoCrear} · ${movCrear}`}
          </h3>
          <button
            className="btn icon"
            onClick={() => {
              setCrearOpen(false);
              setFechaCrear(null);
            }}
          >
            <FiX />
          </button>
        </div>
        <div className="drawer-body">
          <div className="form-grid operation-identity-grid">
            <div className="operation-client-fields">
              <SelectCliente
                value={formOp.nombreCliente}
                onChange={(v) =>
                  setFormOp((f) => ({ ...f, nombreCliente: v }))
                }
                onSelect={(cli) =>
                  setFormOp((f) => ({
                    ...f,
                    nombreCliente: cli?.nombreCliente || "",
                    cliente: cli || null,
                  }))
                }
              />
              <label className="truck-dap-option">
                <input
                  type="checkbox"
                  checked={formOp.dap}
                  onChange={(e) => {
                    const dap = e.target.checked;
                    setFormOp((f) => ({ ...f, dap,
                      dibujo: isTruckLogoAllowed(f.dibujo, movCrear, { dap, operationType: tipoCrear }) ? f.dibujo : "",
                    }));
                  }}
                />
                D.A.P
              </label>
              <FiscalOutChecks operationType={tipoCrear} movement={movCrear} value={formOp}
                onChange={(field, checked) => setFormOp((form) => ({ ...form, [field]: checked }))} />
            </div>
            <LogoSelect
              value={formOp.dibujo}
              onChange={(file) => setFormOp((f) => ({ ...f, dibujo: file }))}
              logos={logos} movement={movCrear} dap={formOp.dap} operationType={tipoCrear}
            />
          </div>

          <div className="truck-creation-details">
            <div className="field truck-count-field">
              <label htmlFor="weekly-create-truck-count">Cantidad de camiones</label>
              <input
                ref={cantidadCamionesCrearRef}
                id="weekly-create-truck-count"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={formOp.cantidadCamiones}
                onChange={(e) =>
                  setFormOp((f) => ({
                    ...f,
                    cantidadCamiones: e.target.value,
                  }))
                }
              />
            </div>
            <div className="field">
              <label htmlFor="weekly-create-truck-observation">Agregar observaciones al camión</label>
              <div className="truck-obs-add">
                <input
                  id="weekly-create-truck-observation"
                  placeholder="Observación"
                  value={contenedorCrear.nombre}
                  onChange={(e) =>
                    setContenedorCrear((c) => ({
                      ...c,
                      nombre: e.target.value,
                    }))
                  }
                />
                <button
                  className="btn"
                  type="button"
                  onClick={agregarContenedorCrear}
                >
                  <FiPlus /> Agregar
                </button>
              </div>
            </div>
          </div>

          {formOp.Contenedores?.length > 0 && (
            <div className="field">
              <label>Observaciones del camión</label>
              <div className="panel modal-panel">
                {formOp.Contenedores.map((c, idx) => (
                  <div key={idx} className="row-line">
                    <span>
                      <b>{getTextoObservacionCamion(c) || "Sin observación"}</b>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="drawer-footer">
          <button
            className="btn"
            onClick={() => {
              setCrearOpen(false);
              setFechaCrear(null);
            }}
          >
            <FiX /> Cancelar
          </button>
          <button className="btn solid" onClick={crear}>
            <FiSave /> Crear
          </button>
        </div>
      </Drawer>

      {/* Drawer edición de contenedores por camión */}
      {editOp && (
        <Drawer
          open={!!editOp}
          onClose={() => setEditOp(null)}
          className="ops-modal ops-modal--wide"
        >
          <div className="drawer-header">
            <div>
              <h3>{`Observaciones ${editOp.nombreCliente || "Sin cliente"} · Cami\u00f3n ${camionActivo + 1}`}</h3>
              <small style={{ color: "var(--muted)" }}>
                {editOp.tipoOperacion} · {editOp.tipo} ·{" "}
                {editOp.fecha?.slice(0, 10)}
              </small>
            </div>
            <button className="btn icon" onClick={() => setEditOp(null)}>
              <FiX />
            </button>
          </div>

          <div className="drawer-body">
            <div className="form-grid operation-identity-grid">
              <SelectCliente
                value={editOp.nombreCliente || ""}
                onChange={(v) =>
                  setEditOp((op) => ({
                    ...op,
                    nombreCliente: v,
                  }))
                }
                onSelect={(cli) =>
                  setEditOp((op) => ({
                    ...op,
                    nombreCliente: cli?.nombreCliente || "",
                    cliente: cli || null,
                  }))
                }
                disabled={!isAdmin}
              />
              <LogoSelect
                value={
                  ((editOp.camiones || []).find(
                    (c) => c.index === camionActivo
                  )?.dibujo ||
                    editOp.dibujo ||
                    "")
                }
                onChange={(file) =>
                  setEditOp((op) => {
                    const camiones = [...(op.camiones || [])];
                    const idx = camiones.findIndex(
                      (c) => c.index === camionActivo
                    );
                    if (idx >= 0) {
                      camiones[idx] = {
                        ...camiones[idx],
                        dibujo: file,
                      };
                    } else {
                      camiones.push({
                        index: camionActivo,
                        dibujo: file,
                      });
                    }
                    return { ...op, camiones };
                  })
                }
                logos={logos} movement={editOp.tipo} dap={editOp.dap} operationType={editOp.tipoOperacion}
                disabled={!puedeEditarLogoActual}
              />
            </div>

            <div className="field">
              <label>{"Agregar observaciones al cami\u00f3n"}</label>
              <div className="truck-obs-add">
                <input
                  disabled={!puedeEditarOpActual}
                  placeholder={"Observaci\u00f3n"}
                  value={contenedorDraft.nombre}
                  onChange={(e) =>
                    setContenedorDraft((c) => ({
                      ...c,
                      nombre: e.target.value,
                    }))
                  }
                />
                <button
                  className="btn"
                  type="button"
                  disabled={!puedeEditarOpActual}
                  onClick={agregarContenedorCamion}
                >
                  <FiPlus /> Agregar
                </button>
              </div>
            </div>

            <div className="field">
              <label>{`Observaciones del cami\u00f3n ${camionActivo + 1}`}</label>
              <div className="panel">
                {(editOp.Contenedores || []).filter(
                  (c) => (c.camionIndex || 0) === camionActivo
                ).length === 0 && (
                  <div className="empty">
                    {"No hay observaciones para este cami\u00f3n."}
                  </div>
                )}

                {(editOp.Contenedores || []).map((c, idx) => {
                  if ((c.camionIndex || 0) !== camionActivo) return null;
                  const estaEditando = contenedorEditando.index === idx;
                  return (
                    <div
                      key={idx}
                      className="row-line truck-obs-row"
                    >
                      <div className="truck-obs-main">
                        {estaEditando ? (
                          <input
                            disabled={!puedeEditarOpActual}
                            placeholder={"Observaci\u00f3n"}
                            value={contenedorEditando.nombre}
                            onChange={(e) =>
                              setContenedorEditando((prev) => ({
                                ...prev,
                                nombre: e.target.value,
                              }))
                            }
                          />
                        ) : (
                          <b>
                            {getTextoObservacionCamion(c) || "Sin observaci\u00f3n"}
                          </b>
                        )}
                      </div>
                      <div className="truck-obs-controls">
                        <select
                          className="truck-obs-state"
                          disabled={!puedeEditarOpActual}
                          value={c.estado || "Completar"}
                          onChange={(e) =>
                            cambiarEstadoContenedor(idx, e.target.value)
                          }
                        >
                          <option value="Completar">Completar</option>
                          <option value="Finalizado">Finalizado</option>
                        </select>
                        <button
                          className="btn icon solid"
                          type="button"
                          disabled={!puedeEditarOpActual}
                          onClick={() =>
                            estaEditando
                              ? guardarEdicionContenedor(idx)
                              : editarContenedor(idx)
                          }
                          aria-label={
                            estaEditando ? "Guardar observación" : "Editar observación"
                          }
                          title={estaEditando ? "Guardar observaci\u00f3n" : "Editar observaci\u00f3n"}
                        >
                          {estaEditando ? <FiSave /> : <FiEdit2 />}
                        </button>
                        <button
                          className="btn icon"
                          type="button"
                          disabled={!puedeEditarOpActual}
                          onClick={() => eliminarContenedor(idx)}
                          title="Eliminar observaci\u00f3n"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {puedeEditarCielFinanzas && (
              <div className="field">
                <label>{CIEL_SIEL_LABEL}</label>
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={!puedeEditarCielFinanzas}
                      checked={sielActivo}
                      onChange={(e) =>
                        setOperacionControl("siel", e.target.checked)
                      }
                    />
                    {SIEL_LABEL}
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={!puedeEditarCielFinanzas}
                      checked={!!facturacionActiva}
                      onChange={(e) =>
                        setOperacionControl(
                          "facturacion",
                          e.target.checked
                        )
                      }
                    />
                    {FACTURACION_LABEL}
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="drawer-footer">
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn danger" onClick={borrarOp} disabled={!isAdmin}>
                <FiTrash2 /> {"Eliminar operaci\u00f3n"}
              </button>
              <button
                className="btn"
                type="button"
                onClick={posponerOperacion}
                title="Pasar solo las observaciones pendientes al d\u00eda siguiente"
                disabled={!isAdmin}
              >
                <FiArrowRightCircle /> {"Pasar a ma\u00f1ana"}
              </button>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn"
                type="button"
                onClick={cerrarTodosContenedoresCamion}
                title="Marcar todas las observaciones de este cami\u00f3n como Finalizado"
                disabled={!puedeEditarOpActual}
              >
                <FiLock /> {"Cerrar cami\u00f3n"}
              </button>
              <button className="btn" onClick={() => setEditOp(null)}>
                <FiX /> Cancelar
              </button>
              <button className="btn solid" onClick={guardarCamion} disabled={!puedeEditarOpActual}>
                <FiSave /> Guardar
              </button>
            </div>
          </div>
        </Drawer>
      )}

      {/* Calendario mensual */}
      <CalendarModal
        open={calOpen}
        onClose={() => setCalOpen(false)}
        valueYmd={fecha}
        onSelectYmd={(ymd) => setFecha(ymd)}
        countsMap={monthCounts}
        opsCountsMap={monthOpsCounts}
        obsCountsMap={monthObsCounts}
        pendingSet={monthPendingSet}
        deposito={depositoActual}
        onGoDia={(ymd) =>
          navigate(
            `/operacionesdia?fecha=${ymd}&deposito=${depositoActual}`
          )
        }
      />
    </div>
  );
}
